import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as Tone from 'tone';
import {
  ArrowLeft, Play, Pause, Square, Circle, SkipBack, SkipForward,
  Volume2, VolumeX, Mic, MicOff, Video, VideoOff,
  Plus, Trash2, Music, Settings, Repeat, Grid,
  PhoneOff, Users, Maximize2, Minimize2, ChevronDown, ChevronRight,
  Headphones, Radio, Save, FolderOpen, ZoomIn, ZoomOut,
  MousePointer2, Scissors, Copy, Hand, Undo2, Redo2,
  Sliders, Clock, Activity, X, Check, RefreshCw,
  FastForward, Rewind, ChevronsLeft, ChevronsRight, Upload, Download,
  Magnet, Waves, Timer, Monitor, Speaker, ToggleLeft, ToggleRight,
  GripVertical, Locate
} from 'lucide-react';
import AdminFreeAccessBar, { generateStudioRoomCode } from '@/components/admin/AdminFreeAccessBar';
import RecentRoomsPanel from '@/components/admin/RecentRoomsPanel';
import BigLevelMeter from '@/components/daw/BigLevelMeter';
import FloatingTransport from '@/components/daw/FloatingTransport';
import { toast } from 'sonner';
import { LiveKitVideoWidget } from '../../components/LiveKitVideo';

const API = process.env.REACT_APP_BACKEND_URL || '';

// ============================================
// AUDIO SETTINGS MODAL - Like Ableton Preferences
// ============================================
// ============================================
// IMPORT AUDIO MODAL - Select destination track & beat
// ============================================
const ImportAudioModal = ({ 
  isOpen, 
  onClose, 
  tracks, 
  currentBeat, 
  onImport 
}) => {
  const [selectedTrack, setSelectedTrack] = useState('new');
  const [beatPosition, setBeatPosition] = useState(0);
  const fileInputRef = useRef(null);
  
  useEffect(() => {
    if (isOpen) {
      setBeatPosition(Math.floor(currentBeat));
    }
  }, [isOpen, currentBeat]);
  
  if (!isOpen) return null;
  
  const audioTracks = tracks.filter(t => t.type === 'audio');
  
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    onImport(file, selectedTrack === 'new' ? null : selectedTrack, beatPosition);
    onClose();
  };
  
  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };
  
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-[#1a1a1a] border border-[#333] rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        {/* Header */}
        <div className="p-4 border-b border-[#333] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Upload className="w-5 h-5 text-amber-400" />
            <h2 className="text-lg font-semibold">Import Audio</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#333] rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Destination Track */}
          <div className="space-y-2">
            <label className="text-sm text-gray-400 font-medium">Destination Track</label>
            <select
              value={selectedTrack}
              onChange={(e) => setSelectedTrack(e.target.value)}
              className="w-full bg-[#222] text-sm px-3 py-2.5 rounded-lg border border-[#333] cursor-pointer hover:border-amber-500 focus:border-amber-500 focus:outline-none"
            >
              <option value="new">+ Create New Track</option>
              {audioTracks.map((track, idx) => (
                <option key={track.id} value={track.id}>
                  Track {idx + 1}: {track.name} {track.waveformData ? '(has audio)' : '(empty)'}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500">
              {selectedTrack === 'new' 
                ? 'A new audio track will be created for this file'
                : 'Audio will be added to the selected track'
              }
            </p>
          </div>
          
          {/* Beat Position */}
          <div className="space-y-2">
            <label className="text-sm text-gray-400 font-medium">Start Position (Beat)</label>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min="0"
                value={beatPosition}
                onChange={(e) => setBeatPosition(Math.max(0, parseInt(e.target.value) || 0))}
                className="flex-1 bg-[#222] text-sm px-3 py-2.5 rounded-lg border border-[#333] focus:border-amber-500 focus:outline-none"
              />
              <button
                onClick={() => setBeatPosition(Math.floor(currentBeat))}
                className="px-3 py-2.5 bg-[#333] hover:bg-[#444] rounded-lg text-sm"
                title="Use current playhead position"
              >
                Use Playhead
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Audio will start at beat {beatPosition} (Bar {Math.floor(beatPosition / 4) + 1}, Beat {(beatPosition % 4) + 1})
            </p>
          </div>
          
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-[#333] hover:bg-[#444] rounded-lg text-sm font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleBrowseClick}
              className="flex-1 px-4 py-2.5 bg-amber-600 hover:bg-amber-500 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
            >
              <FolderOpen className="w-4 h-4" /> Browse Files
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const AudioSettingsModal = ({ 
  isOpen, 
  onClose, 
  audioInputDevices,
  audioOutputDevices,
  selectedInputDevice,
  selectedOutputDevice,
  onInputDeviceChange,
  onOutputDeviceChange,
  sampleRate,
  onSampleRateChange,
  bufferSize,
  onBufferSizeChange,
  inputChannelConfig,
  onInputChannelConfigChange
}) => {
  const [activeTab, setActiveTab] = useState('audio');
  
  // Generate individual mono channels and stereo pairs
  const maxChannels = 18; // For interfaces like X18
  const monoChannels = [];
  const stereoChannelPairs = [];
  
  // Mono: Individual channels 1, 2, 3, 4... up to 18
  for (let i = 1; i <= maxChannels; i++) {
    monoChannels.push({ id: `mono_${i}`, label: `${i}`, channel: i });
  }
  
  // Stereo: Pairs 1/2, 3/4, 5/6... 
  for (let i = 1; i <= maxChannels; i += 2) {
    stereoChannelPairs.push({ id: `stereo_${i}_${i+1}`, label: `${i}/${i+1}`, channels: [i, i+1] });
  }
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-[#1a1a1a] border border-[#333] rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="p-4 border-b border-[#333] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Settings className="w-5 h-5 text-amber-400" />
            <h2 className="text-lg font-semibold">Audio Preferences</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#333] rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-[#333]">
          {['audio', 'inputs', 'outputs'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 text-sm font-medium capitalize ${
                activeTab === tab 
                  ? 'text-amber-400 border-b-2 border-amber-400 bg-[#222]' 
                  : 'text-gray-400 hover:text-white hover:bg-[#222]'
              }`}
            >
              {tab === 'audio' ? 'Audio Device' : tab === 'inputs' ? 'Input Config' : 'Output Config'}
            </button>
          ))}
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'audio' && (
            <div className="space-y-6">
              {/* Driver Type */}
              <div className="grid grid-cols-3 items-center gap-4">
                <label className="text-sm text-gray-400">Driver Type:</label>
                <div className="col-span-2 text-sm bg-[#222] px-3 py-2 rounded border border-[#333]">
                  Web Audio API
                </div>
              </div>
              
              {/* Audio Input Device */}
              <div className="grid grid-cols-3 items-center gap-4">
                <label className="text-sm text-gray-400">Audio Input Device:</label>
                <select
                  value={selectedInputDevice}
                  onChange={(e) => onInputDeviceChange(e.target.value)}
                  className="col-span-2 bg-[#222] text-sm px-3 py-2 rounded border border-[#333] cursor-pointer hover:border-amber-500"
                >
                  <option value="">No Device</option>
                  {audioInputDevices.map(device => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label || `Input ${audioInputDevices.indexOf(device) + 1}`}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Audio Output Device */}
              <div className="grid grid-cols-3 items-center gap-4">
                <label className="text-sm text-gray-400">Audio Output Device:</label>
                <select
                  value={selectedOutputDevice}
                  onChange={(e) => onOutputDeviceChange(e.target.value)}
                  className="col-span-2 bg-[#222] text-sm px-3 py-2 rounded border border-[#333] cursor-pointer hover:border-amber-500"
                >
                  <option value="">System Default</option>
                  {audioOutputDevices.map(device => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label || `Output ${audioOutputDevices.indexOf(device) + 1}`}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Separator */}
              <div className="border-t border-[#333] pt-6">
                <h3 className="text-sm font-medium mb-4">Latency</h3>
              </div>
              
              {/* Sample Rate */}
              <div className="grid grid-cols-3 items-center gap-4">
                <label className="text-sm text-gray-400">Sample Rate:</label>
                <select
                  value={sampleRate}
                  onChange={(e) => onSampleRateChange(parseInt(e.target.value))}
                  className="col-span-2 bg-[#222] text-sm px-3 py-2 rounded border border-[#333] cursor-pointer hover:border-amber-500"
                >
                  <option value="44100">44100 Hz</option>
                  <option value="48000">48000 Hz</option>
                  <option value="88200">88200 Hz</option>
                  <option value="96000">96000 Hz</option>
                </select>
              </div>
              
              {/* Buffer Size */}
              <div className="grid grid-cols-3 items-center gap-4">
                <label className="text-sm text-gray-400">Buffer Size:</label>
                <select
                  value={bufferSize}
                  onChange={(e) => onBufferSizeChange(parseInt(e.target.value))}
                  className="col-span-2 bg-[#222] text-sm px-3 py-2 rounded border border-[#333] cursor-pointer hover:border-amber-500"
                >
                  <option value="64">64 Samples</option>
                  <option value="128">128 Samples</option>
                  <option value="256">256 Samples</option>
                  <option value="512">512 Samples</option>
                  <option value="1024">1024 Samples</option>
                  <option value="2048">2048 Samples</option>
                </select>
              </div>
              
              {/* Calculated Latency */}
              <div className="grid grid-cols-3 items-center gap-4">
                <label className="text-sm text-gray-400">Estimated Latency:</label>
                <div className="col-span-2 text-sm text-gray-300">
                  {((bufferSize / sampleRate) * 1000 * 2).toFixed(1)} ms
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'inputs' && (
            <div className="space-y-6">
              <p className="text-sm text-gray-400 mb-4">
                Select which hardware inputs you want to use. For multi-channel interfaces (like X18), 
                you can enable individual channels to assign them to tracks.
              </p>
              
              <div className="text-xs text-amber-400 mb-4 p-2 bg-amber-500/10 rounded border border-amber-500/30">
                Note: Web Audio API accesses your interface as a single device. Channel separation 
                requires the interface to present individual channels (driver dependent).
              </div>
              
              {/* Mono Inputs */}
              <div>
                <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Mic className="w-4 h-4" /> Mono Inputs
                </h3>
                <div className="grid grid-cols-6 gap-2">
                  {monoChannels.map(ch => (
                    <label
                      key={ch.id}
                      className={`flex items-center justify-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                        inputChannelConfig?.mono?.includes(ch.id)
                          ? 'bg-green-600/20 border border-green-500/50'
                          : 'bg-[#222] border border-[#333] hover:border-[#444]'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={inputChannelConfig?.mono?.includes(ch.id) || false}
                        onChange={(e) => {
                          const newMono = e.target.checked
                            ? [...(inputChannelConfig?.mono || []), ch.id]
                            : (inputChannelConfig?.mono || []).filter(id => id !== ch.id);
                          onInputChannelConfigChange({ ...inputChannelConfig, mono: newMono });
                        }}
                        className="accent-green-500"
                      />
                      <span className="text-sm font-medium">{ch.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Stereo Inputs */}
              <div className="mt-6">
                <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Headphones className="w-4 h-4" /> Stereo Inputs
                </h3>
                <div className="grid grid-cols-3 gap-2">
                  {stereoChannelPairs.map(pair => (
                    <label
                      key={pair.id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                        inputChannelConfig?.stereo?.includes(pair.id)
                          ? 'bg-blue-600/20 border border-blue-500/50'
                          : 'bg-[#222] border border-[#333] hover:border-[#444]'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={inputChannelConfig?.stereo?.includes(pair.id) || false}
                        onChange={(e) => {
                          const newStereo = e.target.checked
                            ? [...(inputChannelConfig?.stereo || []), pair.id]
                            : (inputChannelConfig?.stereo || []).filter(id => id !== pair.id);
                          onInputChannelConfigChange({ ...inputChannelConfig, stereo: newStereo });
                        }}
                        className="accent-blue-500"
                      />
                      <span className="text-sm">{pair.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <p className="text-xs text-gray-500 mt-4">
                Deactivating unused inputs can help reduce CPU load. Stereo pairs can also be used as two mono inputs.
              </p>
            </div>
          )}
          
          {activeTab === 'outputs' && (
            <div className="space-y-6">
              <p className="text-sm text-gray-400 mb-4">
                Configure your output routing. For multi-channel interfaces, you can assign different 
                outputs to the master, cue, or individual tracks.
              </p>
              
              {/* Master Output */}
              <div className="grid grid-cols-3 items-center gap-4">
                <label className="text-sm text-gray-400">Master Out:</label>
                <select
                  className="col-span-2 bg-[#222] text-sm px-3 py-2 rounded border border-[#333] cursor-pointer hover:border-amber-500"
                  defaultValue="1/2"
                >
                  <option value="1/2">Output 1/2</option>
                  <option value="3/4">Output 3/4</option>
                  <option value="5/6">Output 5/6</option>
                  <option value="7/8">Output 7/8</option>
                </select>
              </div>
              
              {/* Cue Output */}
              <div className="grid grid-cols-3 items-center gap-4">
                <label className="text-sm text-gray-400">Cue Out:</label>
                <select
                  className="col-span-2 bg-[#222] text-sm px-3 py-2 rounded border border-[#333] cursor-pointer hover:border-amber-500"
                  defaultValue="3/4"
                >
                  <option value="off">Off</option>
                  <option value="1/2">Output 1/2</option>
                  <option value="3/4">Output 3/4</option>
                  <option value="5/6">Output 5/6</option>
                  <option value="7/8">Output 7/8</option>
                </select>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-[#333] flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#333] hover:bg-[#444] rounded-lg text-sm"
          >
            Close
          </button>
          <button
            onClick={() => {
              toast.success('Audio settings saved');
              onClose();
            }}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 rounded-lg text-sm font-medium"
          >
            Apply
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// ============================================
// VIDEO PIP COMPONENT - Always visible
// ============================================
const VideoPiP = ({ stream, label, position, isMuted = false, isMinimized, onToggleMinimize }) => {
  const videoRef = useRef(null);
  
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(() => {});
    }
  }, [stream]);
  
  const positionStyles = {
    'top-left': { top: '80px', left: '16px' },
    'top-right': { top: '80px', right: '16px' },
    'bottom-left': { bottom: '180px', left: '16px' },
    'bottom-right': { bottom: '180px', right: '16px' },
  };
  
  return (
    <motion.div 
      className="fixed z-50 shadow-2xl"
      style={positionStyles[position]}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {isMinimized ? (
        <button
          onClick={onToggleMinimize}
          className="bg-[#1a1a1a] border border-[#333] p-3 rounded-lg hover:bg-[#2a2a2a]"
          title={`Show ${label}`}
        >
          <Video className="w-5 h-5 text-amber-400" />
        </button>
      ) : (
        <div className={`w-44 bg-[#1a1a1a] rounded-lg overflow-hidden border-2 ${stream ? 'border-amber-500' : 'border-gray-600'}`}>
          {stream ? (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted={isMuted}
              className="w-full h-32 object-cover bg-black"
              style={{ transform: isMuted ? 'scaleX(-1)' : 'none' }}
            />
          ) : (
            <div className="w-full h-32 bg-[#0a0a0a] flex flex-col items-center justify-center">
              <Video className="w-8 h-8 text-gray-600 mb-2" />
              <span className="text-xs text-gray-500">Waiting...</span>
            </div>
          )}
          <div className="flex items-center justify-between px-2 py-1 bg-[#0a0a0a]">
            <span className={`text-xs font-medium ${stream ? 'text-amber-400' : 'text-gray-500'}`}>{label}</span>
            <button 
              onClick={onToggleMinimize}
              className="p-1 hover:bg-[#333] rounded"
            >
              <Minimize2 className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

// ============================================
// REAL-TIME WAVEFORM DISPLAY (during recording)
// ============================================
const LiveWaveform = ({ analyser, isRecording, color }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const dataHistoryRef = useRef([]);
  
  useEffect(() => {
    if (!analyser || !canvasRef.current || !isRecording) {
      dataHistoryRef.current = [];
      return;
    }
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    
    const draw = () => {
      if (!isRecording) return;
      
      analyser.getByteTimeDomainData(dataArray);
      
      // Calculate RMS level
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const v = (dataArray[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / dataArray.length);
      
      // Add to history
      dataHistoryRef.current.push(rms);
      if (dataHistoryRef.current.length > canvas.width) {
        dataHistoryRef.current.shift();
      }
      
      // Clear and draw
      ctx.fillStyle = '#0a0a0a';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw waveform
      ctx.strokeStyle = color || '#22c55e';
      ctx.lineWidth = 1;
      ctx.beginPath();
      
      const history = dataHistoryRef.current;
      for (let i = 0; i < history.length; i++) {
        const x = i;
        const y = (1 - history[i]) * canvas.height / 2;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
      
      // Mirror
      ctx.beginPath();
      for (let i = 0; i < history.length; i++) {
        const x = i;
        const y = canvas.height / 2 + history[i] * canvas.height / 2;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
      
      animationRef.current = requestAnimationFrame(draw);
    };
    
    draw();
    
    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [analyser, isRecording, color]);
  
  return (
    <canvas 
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      width={1000}
      height={96}
    />
  );
};

// ============================================
// TRACK COMPONENT - DAW Style
// ============================================
const Track = ({ 
  track, 
  index,
  isSelected, 
  onSelect, 
  onDelete,
  onMute,
  onSolo,
  onArm,
  onVolumeChange,
  onNameChange,
  onInputChange,
  onOpenFx,
  onMoveAudio,
  onReorderTrack,
  onMoveAudioToTrack,
  tempo,
  isRecording,
  analyser,
  zoom,
  measures,
  beatsPerMeasure,
  pixelsPerBeat,
  currentBeat,
  onTimelineClick,
  audioInputDevices,
  inputChannelConfig,
  selectionStart,
  selectionEnd,
  onSetSelection,
  totalTracks
}) => {
  const waveformRef = useRef(null);
  const waveformContainerRef = useRef(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectStartX, setSelectStartX] = useState(null);
  const [isDraggingClip, setIsDraggingClip] = useState(false);
  const [isDraggingTrack, setIsDraggingTrack] = useState(false);
  const [dragOverTrack, setDragOverTrack] = useState(false);
  const clipDragRef = useRef({ startX: 0, originalBeat: 0 });
  
  // Draw recorded waveform
  useEffect(() => {
    if (!waveformRef.current || !track.waveformData) return;
    
    const canvas = waveformRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    const data = track.waveformData;
    ctx.strokeStyle = track.color;
    ctx.lineWidth = 1;
    ctx.beginPath();
    
    const step = Math.max(1, Math.floor(data.length / canvas.width));
    for (let i = 0; i < canvas.width; i++) {
      const idx = Math.min(i * step, data.length - 1);
      const value = data[idx] || 0;
      const y = (1 - value) * canvas.height / 2;
      if (i === 0) ctx.moveTo(i, y);
      else ctx.lineTo(i, y);
    }
    ctx.stroke();
    
    // Mirror
    ctx.beginPath();
    for (let i = 0; i < canvas.width; i++) {
      const idx = Math.min(i * step, data.length - 1);
      const value = data[idx] || 0;
      const y = canvas.height / 2 + value * canvas.height / 2;
      if (i === 0) ctx.moveTo(i, y);
      else ctx.lineTo(i, y);
    }
    ctx.stroke();
  }, [track.waveformData, track.color]);
  
  const trackWidth = measures * beatsPerMeasure * pixelsPerBeat * zoom;
  
  // Handle waveform selection
  const handleWaveformMouseDown = (e) => {
    if (!track.audioBuffer || !isSelected) return;
    const rect = waveformContainerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    setIsSelecting(true);
    setSelectStartX(x);
    const duration = track.audioBuffer.duration;
    const startTime = (x / rect.width) * duration;
    onSetSelection?.(track.id, startTime, startTime);
  };
  
  const handleWaveformMouseMove = (e) => {
    if (!isSelecting || !track.audioBuffer) return;
    const rect = waveformContainerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const duration = track.audioBuffer.duration;
    const startTime = (selectStartX / rect.width) * duration;
    const endTime = (x / rect.width) * duration;
    onSetSelection?.(track.id, startTime, endTime);
  };
  
  const handleWaveformMouseUp = () => {
    setIsSelecting(false);
  };
  
  useEffect(() => {
    if (isSelecting) {
      const handleUp = () => setIsSelecting(false);
      window.addEventListener('mouseup', handleUp);
      return () => window.removeEventListener('mouseup', handleUp);
    }
  }, [isSelecting]);
  
  // Calculate selection overlay position
  const getSelectionOverlay = () => {
    if (!isSelected || selectionStart === null || selectionEnd === null || !track.audioBuffer) return null;
    const duration = track.audioBuffer.duration;
    const left = (Math.min(selectionStart, selectionEnd) / duration) * 100;
    const width = (Math.abs(selectionEnd - selectionStart) / duration) * 100;
    return { left: `${left}%`, width: `${width}%` };
  };
  
  const selectionStyle = getSelectionOverlay();
  
  // Handle timeline click to move playhead
  const handleTimelineClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const beat = x / (pixelsPerBeat * zoom);
    onTimelineClick(Math.max(0, beat));
  };
  
  // Handle dragging audio clip
  const handleClipMouseDown = (e) => {
    if (!track.waveformData) return;
    e.stopPropagation();
    
    setIsDraggingClip(true);
    clipDragRef.current = {
      startX: e.clientX,
      originalBeat: track.audioStartBeat || 0
    };
  };
  
  // Handle clip drag move
  useEffect(() => {
    if (!isDraggingClip) return;
    
    const handleMouseMove = (e) => {
      const deltaX = e.clientX - clipDragRef.current.startX;
      const deltaBeat = deltaX / (pixelsPerBeat * zoom);
      const newBeat = Math.max(0, clipDragRef.current.originalBeat + deltaBeat);
      onMoveAudio?.(track.id, newBeat);
    };
    
    const handleMouseUp = () => {
      setIsDraggingClip(false);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingClip, pixelsPerBeat, zoom, track.id, onMoveAudio]);
  
  return (
    <div 
      data-testid={`track-row-${index}`}
      className={`flex border-b border-[#2a2a2a] transition-all duration-150 ${
        isSelected 
          ? 'bg-amber-900/30 border-l-4 border-l-amber-400 shadow-[inset_0_0_20px_rgba(251,191,36,0.1)]' 
          : 'hover:bg-[#1a1a1a] border-l-4 border-l-transparent'
      } ${track.armed ? 'bg-red-950/30' : ''} ${dragOverTrack ? 'ring-2 ring-blue-400 ring-inset bg-blue-900/20' : ''}`}
      onClick={() => onSelect(track.id)}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOverTrack(true);
      }}
      onDragLeave={() => setDragOverTrack(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOverTrack(false);
        const sourceTrackId = e.dataTransfer.getData('trackId');
        const sourceType = e.dataTransfer.getData('type');
        
        if (sourceType === 'track' && sourceTrackId !== track.id) {
          // Reorder tracks
          onReorderTrack?.(sourceTrackId, index);
        } else if (sourceType === 'audio' && sourceTrackId !== track.id) {
          // Move audio to this track
          const beatPosition = parseFloat(e.dataTransfer.getData('beat') || '0');
          onMoveAudioToTrack?.(sourceTrackId, track.id, beatPosition);
        }
      }}
    >
      {/* Track Header */}
      <div className={`w-80 min-w-80 p-3 border-r border-[#2a2a2a] flex flex-col gap-2 overflow-hidden ${
        isSelected ? 'bg-[#252525]' : 'bg-[#1a1a1a]'
      }`}>
        <div className="flex items-center gap-1">
          {/* Drag Handle for Reordering - PROMINENT */}
          <div
            draggable
            onDragStart={(e) => {
              e.stopPropagation();
              e.dataTransfer.setData('trackId', track.id);
              e.dataTransfer.setData('type', 'track');
              e.dataTransfer.effectAllowed = 'move';
              setIsDraggingTrack(true);
            }}
            onDragEnd={() => setIsDraggingTrack(false)}
            className={`cursor-grab active:cursor-grabbing p-1.5 rounded mr-1 transition-all ${
              isDraggingTrack 
                ? 'bg-amber-500 scale-110' 
                : 'bg-[#333] hover:bg-amber-600 hover:scale-105'
            }`}
            title="Drag to reorder track"
            data-testid={`track-drag-handle-${index}`}
          >
            <GripVertical className={`w-4 h-4 ${isDraggingTrack ? 'text-black' : 'text-gray-300'}`} />
          </div>
          <div className="w-3 h-3 rounded" style={{ backgroundColor: track.color }} />
          <input
            type="text"
            value={track.name}
            onChange={(e) => onNameChange(track.id, e.target.value)}
            onClick={(e) => e.stopPropagation()}
            className="bg-transparent text-sm font-medium flex-1 min-w-0 outline-none hover:bg-[#333] px-1 rounded"
          />
        </div>
        
        {/* Track Controls */}
        <div className="flex items-center gap-1 mt-1">
          <button 
            onClick={(e) => { e.stopPropagation(); onMute(track.id); }}
            className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${track.muted ? 'bg-amber-500 text-black' : 'bg-[#333] text-gray-400 hover:bg-[#444]'}`}
            title="Mute"
          >
            M
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); onSolo(track.id); }}
            className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${track.solo ? 'bg-yellow-500 text-black' : 'bg-[#333] text-gray-400 hover:bg-[#444]'}`}
            title="Solo"
          >
            S
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); onArm(track.id); }}
            className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${track.armed ? 'bg-red-500 text-white animate-pulse' : 'bg-[#333] text-gray-400 hover:bg-[#444]'}`}
            title="Record Arm"
          >
            R
          </button>
          
          {/* Volume Slider */}
          <div className="flex items-center gap-1 ml-auto">
            <span className="text-[9px] text-gray-500 w-6 text-right">{track.volume}%</span>
            <input
              type="range"
              min="0"
              max="100"
              value={track.volume}
              onChange={(e) => { e.stopPropagation(); onVolumeChange(track.id, parseInt(e.target.value)); }}
              onClick={(e) => e.stopPropagation()}
              className="w-14 h-1 accent-amber-500 cursor-pointer"
            />
          </div>
          
          <button 
            onClick={(e) => { e.stopPropagation(); onDelete(track.id); }}
            className="p-1 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded ml-1"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
        
        {/* Input Source & FX */}
        <div className="flex items-center gap-1 mt-1">
          {track.type === 'audio' ? (
            <div className="flex items-center gap-1 flex-1">
              <Mic className="w-3 h-3 text-gray-500" />
              <select
                value={track.inputChannel || ''}
                onChange={(e) => { e.stopPropagation(); onInputChange(track.id, e.target.value); }}
                onClick={(e) => e.stopPropagation()}
                className="bg-[#222] text-[10px] text-gray-400 rounded px-1 py-0.5 flex-1 min-w-0 cursor-pointer hover:bg-[#333] border border-[#333]"
              >
                <option value="">Select Input</option>
                {/* Show configured mono channels - individual channels */}
                {inputChannelConfig?.mono?.length > 0 && (
                  <optgroup label="Mono">
                    {inputChannelConfig.mono.map(ch => {
                      const num = ch.replace('mono_', '');
                      return (
                        <option key={ch} value={ch}>Input {num}</option>
                      );
                    })}
                  </optgroup>
                )}
                {/* Show configured stereo channels - pairs */}
                {inputChannelConfig?.stereo?.length > 0 && (
                  <optgroup label="Stereo">
                    {inputChannelConfig.stereo.map(ch => {
                      const nums = ch.replace('stereo_', '').split('_');
                      return (
                        <option key={ch} value={ch}>Input {nums[0]}/{nums[1]}</option>
                      );
                    })}
                  </optgroup>
                )}
                {/* Fallback to device list if no channels configured */}
                {(!inputChannelConfig?.mono?.length && !inputChannelConfig?.stereo?.length) && (
                  audioInputDevices.length === 0 ? (
                    <option value="" disabled>No devices - Open Audio Settings</option>
                  ) : (
                    audioInputDevices.map(device => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || `Input ${audioInputDevices.indexOf(device) + 1}`}
                      </option>
                    ))
                  )
                )}
              </select>
            </div>
          ) : (
            <div className="flex items-center gap-1 text-[10px] text-gray-500">
              <Music className="w-3 h-3" /> {track.instrument || 'Piano'}
            </div>
          )}
          
          {/* FX Button */}
          <button
            onClick={(e) => { e.stopPropagation(); onOpenFx(track.id); }}
            className={`px-1.5 py-0.5 text-[9px] font-bold rounded ${track.effects?.length > 0 ? 'bg-purple-600 text-white' : 'bg-[#333] text-gray-400 hover:bg-purple-600/50'}`}
            title="Effects"
          >
            FX{track.effects?.length > 0 && ` (${track.effects.length})`}
          </button>
        </div>
      </div>
      
      {/* Track Content / Timeline - CLICKABLE for playhead placement */}
      <div 
        ref={waveformContainerRef}
        className={`flex-1 h-24 relative overflow-hidden ${
          isSelected ? 'ring-2 ring-inset ring-amber-400 bg-amber-950/30' : ''
        } cursor-crosshair`}
        style={{ minWidth: trackWidth }}
        onClick={(e) => {
          // Always select this track on click
          onSelect(track.id);
          
          // Calculate beat position from click and move playhead
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const clickedBeat = x / (pixelsPerBeat * zoom);
          onTimelineClick(Math.max(0, clickedBeat));
        }}
        onMouseDown={handleWaveformMouseDown}
        onMouseMove={handleWaveformMouseMove}
        onMouseUp={handleWaveformMouseUp}
      >
        {/* Grid Lines */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none">
          {Array.from({ length: measures * beatsPerMeasure + 1 }).map((_, i) => (
            <line
              key={i}
              x1={i * pixelsPerBeat * zoom}
              y1={0}
              x2={i * pixelsPerBeat * zoom}
              y2="100%"
              stroke={i % beatsPerMeasure === 0 ? '#333' : '#1f1f1f'}
              strokeWidth={i % beatsPerMeasure === 0 ? 1 : 0.5}
            />
          ))}
        </svg>
        
        {/* Live Waveform (during recording) */}
        {track.armed && isRecording && analyser && (
          <LiveWaveform analyser={analyser} isRecording={isRecording} color={track.color} />
        )}
        
        {/* Recorded Waveform - DRAGGABLE between tracks and along timeline */}
        {track.type === 'audio' && track.waveformData && !track.armed && (
          <div 
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData('trackId', track.id);
              e.dataTransfer.setData('type', 'audio');
              e.dataTransfer.setData('beat', String(track.audioStartBeat || 0));
              e.stopPropagation();
            }}
            className={`absolute top-1 bottom-1 cursor-grab active:cursor-grabbing rounded-lg overflow-hidden border-2 ${isDraggingClip ? 'border-blue-400 opacity-70' : 'border-transparent hover:border-blue-400/50'}`}
            style={{ 
              left: `${(track.audioStartBeat || 0) * pixelsPerBeat * zoom}px`,
              width: track.audioBuffer ? `${track.audioBuffer.duration * (tempo / 60) * pixelsPerBeat * zoom}px` : 'auto'
            }}
            onMouseDown={handleClipMouseDown}
            title="Drag to move clip (up/down to move to other tracks)"
          >
            <canvas 
              ref={waveformRef}
              className="w-full h-full pointer-events-none"
              width={trackWidth}
              height={96}
            />
            {/* Clip handles */}
            <div className="absolute left-0 top-0 bottom-0 w-2 bg-blue-500/50 hover:bg-blue-500 cursor-ew-resize" />
            <div className="absolute right-0 top-0 bottom-0 w-2 bg-blue-500/50 hover:bg-blue-500 cursor-ew-resize" />
            {/* Track name label on clip */}
            <div className="absolute top-1 left-2 text-[9px] text-white/70 bg-black/50 px-1 rounded">
              {track.name}
            </div>
            {/* Selection overlay */}
            {selectionStyle && (
              <div 
                className="absolute top-0 bottom-0 bg-blue-500/30 border-l border-r border-blue-400"
                style={selectionStyle}
              />
            )}
          </div>
        )}
        
        {/* MIDI Notes */}
        {track.type === 'midi' && track.notes?.length > 0 && (
          <div className="absolute inset-0 pointer-events-none">
            {track.notes.map((note, i) => (
              <div
                key={i}
                className="absolute h-2 rounded-sm"
                style={{
                  left: `${note.beat * pixelsPerBeat * zoom}px`,
                  top: `${40 + Math.random() * 40}px`,
                  width: `${(note.duration || 0.5) * pixelsPerBeat * zoom}px`,
                  backgroundColor: track.color
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// VU METER COMPONENT
// ============================================
const VUMeter = ({ level = 0, peak = 0, vertical = true }) => {
  const segments = 12;
  const segmentHeight = 100 / segments;
  
  if (!vertical) {
    // Horizontal meter for input/output indicators
    return (
      <div className="flex gap-px h-2 w-full bg-[#0a0a0a] rounded-sm p-px">
        {Array.from({ length: segments }).map((_, i) => {
          const threshold = (i / segments) * 100;
          const isActive = level > threshold;
          let color = 'bg-gray-700';
          if (isActive) {
            if (threshold >= 80) color = 'bg-red-500';
            else if (threshold >= 60) color = 'bg-yellow-500';
            else color = 'bg-green-500';
          }
          return <div key={i} className={`flex-1 rounded-sm ${color}`} />;
        })}
      </div>
    );
  }
  
  return (
    <div className="flex gap-0.5 h-full">
      {[0, 1].map(channel => (
        <div key={channel} className="w-1.5 h-full bg-[#0a0a0a] rounded-sm flex flex-col-reverse gap-px p-px">
          {Array.from({ length: segments }).map((_, i) => {
            const threshold = (i / segments) * 100;
            const isActive = level > threshold;
            const isPeak = peak > threshold && peak <= threshold + segmentHeight;
            
            let color = 'bg-gray-700';
            if (isActive || isPeak) {
              if (threshold >= 80) color = 'bg-red-500';
              else if (threshold >= 60) color = 'bg-yellow-500';
              else color = 'bg-green-500';
            }
            if (isPeak && !isActive) color = color.replace('500', '400');
            
            return (
              <div 
                key={i} 
                className={`flex-1 rounded-sm transition-colors duration-75 ${color}`}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
};

// ============================================
// VERTICAL EQ KNOB (smaller, stacked)
// ============================================
const VerticalEQKnob = ({ label, value, onChange, color = 'amber' }) => {
  const [isDragging, setIsDragging] = useState(false);
  const startYRef = useRef(0);
  const startValueRef = useRef(0);
  
  const handleMouseDown = (e) => {
    e.stopPropagation();
    setIsDragging(true);
    startYRef.current = e.clientY;
    startValueRef.current = value;
  };
  
  useEffect(() => {
    if (isDragging) {
      const handleMove = (e) => {
        const delta = startYRef.current - e.clientY;
        const newValue = Math.max(-12, Math.min(12, startValueRef.current + delta * 0.2));
        onChange(newValue);
      };
      const handleUp = () => setIsDragging(false);
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleUp);
      return () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleUp);
      };
    }
  }, [isDragging, onChange]);
  
  const rotation = (value / 12) * 135;
  const colors = {
    blue: 'bg-blue-400 border-blue-500/50',
    green: 'bg-green-400 border-green-500/50',
    red: 'bg-red-400 border-red-500/50',
    amber: 'bg-amber-400 border-amber-500/50'
  };
  
  return (
    <div className="flex items-center gap-1 w-full">
      <span className="text-[7px] text-gray-500 w-4">{label}</span>
      <div 
        onMouseDown={handleMouseDown}
        className={`w-5 h-5 rounded-full bg-[#333] border ${colors[color].split(' ')[1]} relative cursor-ns-resize hover:border-white/50 select-none flex-shrink-0`}
        title={`${label}: ${value > 0 ? '+' : ''}${value.toFixed(1)} dB`}
      >
        <div className="absolute inset-0.5 rounded-full bg-[#222] flex items-center justify-center">
          <div className={`w-0.5 h-2 ${colors[color].split(' ')[0]} rounded origin-bottom`} style={{ transform: `rotate(${rotation}deg)` }} />
        </div>
      </div>
      <span className="text-[7px] text-gray-400 w-5 text-right">{value > 0 ? '+' : ''}{value.toFixed(0)}</span>
    </div>
  );
};

// ============================================
// MIXER CHANNEL - Enhanced with Vertical EQ, I/O, FX
// ============================================
const MixerChannel = ({ 
  track, 
  onVolumeChange, 
  onMute, 
  onSolo,
  onArm,
  onPanChange, 
  onEQChange,
  onFxClick,
  meterLevel = 0, 
  meterPeak = 0,
  inputLevel = 0,
  isRecording = false
}) => {
  const [isDraggingPan, setIsDraggingPan] = useState(false);
  const panKnobRef = useRef(null);
  
  const eq = track.eq || { low: 0, mid: 0, high: 0 };
  
  const handlePanDrag = (e) => {
    if (!isDraggingPan || !panKnobRef.current) return;
    const rect = panKnobRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const angle = Math.atan2(e.clientY - centerY, e.clientX - centerX);
    let degrees = (angle * 180 / Math.PI) + 90;
    if (degrees > 180) degrees -= 360;
    const pan = Math.max(-1, Math.min(1, degrees / 135));
    onPanChange(track.id, pan);
  };
  
  useEffect(() => {
    if (isDraggingPan) {
      const handleMove = (e) => handlePanDrag(e);
      const handleUp = () => setIsDraggingPan(false);
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleUp);
      return () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleUp);
      };
    }
  }, [isDraggingPan]);
  
  return (
    <div className="w-20 bg-[#1a1a1a] rounded-lg p-1.5 flex flex-col items-center gap-1">
      {/* Track Color & Label */}
      <div className="w-full flex items-center gap-1 mb-0.5">
        <div className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: track.color }} />
        <span className="text-[8px] text-gray-400 truncate flex-1">{track.name}</span>
      </div>
      
      {/* Input Level Meter */}
      <div className="w-full">
        <span className="text-[6px] text-gray-600 block mb-0.5">IN</span>
        <VUMeter level={inputLevel} vertical={false} />
      </div>
      
      {/* Vertical 3-Band EQ */}
      <div className="w-full bg-[#0f0f0f] rounded p-1 space-y-0.5">
        <VerticalEQKnob 
          label="HI" 
          value={eq.high} 
          onChange={(v) => onEQChange?.(track.id, { ...eq, high: v })}
          color="blue"
        />
        <VerticalEQKnob 
          label="MD" 
          value={eq.mid} 
          onChange={(v) => onEQChange?.(track.id, { ...eq, mid: v })}
          color="green"
        />
        <VerticalEQKnob 
          label="LO" 
          value={eq.low} 
          onChange={(v) => onEQChange?.(track.id, { ...eq, low: v })}
          color="red"
        />
      </div>
      
      {/* FX Button */}
      <button
        onClick={() => onFxClick?.(track.id)}
        className={`w-full py-0.5 rounded text-[7px] font-bold transition-colors ${
          track.effects?.length > 0 
            ? 'bg-purple-600 text-white' 
            : 'bg-[#333] text-gray-400 hover:bg-[#444]'
        }`}
      >
        FX {track.effects?.length > 0 && `(${track.effects.length})`}
      </button>
      
      {/* Pan Knob */}
      <div 
        ref={panKnobRef}
        onMouseDown={() => setIsDraggingPan(true)}
        className="w-7 h-7 rounded-full bg-[#333] border border-[#444] relative cursor-pointer hover:border-amber-500 select-none"
        title={`Pan: ${((track.pan || 0) * 100).toFixed(0)}%`}
      >
        <div className="absolute inset-0.5 rounded-full bg-[#222] flex items-center justify-center">
          <div className="w-0.5 h-2.5 bg-amber-400 rounded origin-bottom" style={{ transform: `rotate(${(track.pan || 0) * 135}deg)` }} />
        </div>
      </div>
      <span className="text-[7px] text-gray-500">
        {((track.pan || 0) * 100).toFixed(0) < 0 ? `${Math.abs((track.pan || 0) * 100).toFixed(0)}L` : ((track.pan || 0) * 100).toFixed(0) > 0 ? `${((track.pan || 0) * 100).toFixed(0)}R` : 'C'}
      </span>
      
      {/* Volume Fader with VU Meter */}
      <div className="flex gap-0.5 items-end">
        <VUMeter level={meterLevel} peak={meterPeak} />
        <div className="h-24 w-3 bg-[#0a0a0a] rounded relative flex flex-col items-center py-1">
          <input
            type="range"
            min="0"
            max="100"
            value={track.volume}
            onChange={(e) => onVolumeChange(track.id, parseInt(e.target.value))}
            className="h-20 w-1 accent-amber-500 cursor-pointer"
            style={{ writingMode: 'vertical-lr', direction: 'rtl' }}
          />
        </div>
      </div>
      <span className="text-[8px] text-gray-400 font-mono">{track.volume}</span>
      
      {/* Output Level Meter */}
      <div className="w-full">
        <span className="text-[6px] text-gray-600 block mb-0.5">OUT</span>
        <VUMeter level={meterLevel} vertical={false} />
      </div>
      
      {/* Mute/Solo/Record Arm Buttons */}
      <div className="flex gap-1">
        <button 
          onClick={() => onMute(track.id)}
          className={`w-5 h-5 text-[8px] font-bold rounded ${track.muted ? 'bg-amber-500 text-black' : 'bg-[#333] hover:bg-[#444]'}`}
          title="Mute"
        >
          M
        </button>
        <button 
          onClick={() => onSolo(track.id)}
          className={`w-5 h-5 text-[8px] font-bold rounded ${track.solo ? 'bg-yellow-500 text-black' : 'bg-[#333] hover:bg-[#444]'}`}
          title="Solo"
        >
          S
        </button>
        <button 
          onClick={() => onArm?.(track.id)}
          className={`w-5 h-5 text-[8px] font-bold rounded transition-all ${
            track.armed 
              ? isRecording 
                ? 'bg-red-500 text-white animate-pulse ring-2 ring-red-400' 
                : 'bg-red-500 text-white'
              : 'bg-[#333] hover:bg-red-900 text-gray-400 hover:text-white'
          }`}
          title={track.armed ? 'Disarm recording' : 'Arm for recording'}
        >
          R
        </button>
      </div>
    </div>
  );
};

// ============================================
// MAIN STUDIO SESSION COMPONENT
// ============================================
const StudioSession = () => {
  const { roomCode } = useParams();
  
  // LiveKit video state (replaces old WebRTC VideoContext)
  const [showLiveKitVideo, setShowLiveKitVideo] = useState(true);
  const [sessionActive, setSessionActive] = useState(true); // Auto-start session
  const [clientConnected, setClientConnected] = useState(false);
  
  // Generate room code from URL param or create new one
  const [liveKitRoomCode, setLiveKitRoomCode] = useState(() => {
    if (roomCode) return roomCode.toUpperCase();
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  });
  
  // DAW State - Pre-loaded with 4 tracks (2 audio, 2 MIDI)
  const trackColors = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];
  
  const initialTracks = [
    { id: 'track_audio_1', name: 'Audio 1', type: 'audio', color: trackColors[0], volume: 80, pan: 0, muted: false, solo: false, armed: false, effects: [], regions: [] },
    { id: 'track_audio_2', name: 'Audio 2', type: 'audio', color: trackColors[1], volume: 80, pan: 0, muted: false, solo: false, armed: false, effects: [], regions: [] },
    { id: 'track_midi_1', name: 'MIDI 1', type: 'midi', color: trackColors[2], volume: 80, pan: 0, muted: false, solo: false, armed: false, effects: [], regions: [] },
    { id: 'track_midi_2', name: 'MIDI 2', type: 'midi', color: trackColors[3], volume: 80, pan: 0, muted: false, solo: false, armed: false, effects: [], regions: [] },
  ];
  
  const [tracks, setTracks] = useState(initialTracks);
  const [selectedTrackId, setSelectedTrackId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [currentBeat, setCurrentBeat] = useState(0);
  const [tempo, setTempo] = useState(120);
  const [measures, setMeasures] = useState(32);
  const [beatsPerMeasure, setBeatsPerMeasure] = useState(4);
  const [zoom, setZoom] = useState(1);
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [loopStart, setLoopStart] = useState(0);
  const [loopEnd, setLoopEnd] = useState(16);
  const [showMixer, setShowMixer] = useState(true);
  const [selectedTool, setSelectedTool] = useState('select');
  const [masterVolume, setMasterVolume] = useState(80);
  
  // Cursor Stop Mode: 'stay' | 'return-start' | 'return-zero'
  const [cursorStopMode, setCursorStopMode] = useState('stay');
  const [showCursorMenu, setShowCursorMenu] = useState(false);
  const playStartBeatRef = useRef(0); // Track where playback started
  
  // Metronome
  const [metronomeEnabled, setMetronomeEnabled] = useState(false);
  const [metronomeVolume, setMetronomeVolume] = useState(50);
  const metronomeRef = useRef(null);
  
  // Quantize
  const [quantizeValue, setQuantizeValue] = useState('1/16');
  const [showQuantizeMenu, setShowQuantizeMenu] = useState(false);
  const quantizeOptions = ['Off', '1/4', '1/8', '1/16', '1/32', '1/64'];
  
  // FX Panel
  const [showFxPanel, setShowFxPanel] = useState(false);
  const [selectedTrackForFx, setSelectedTrackForFx] = useState(null);
  
  // Waveform Editing
  const [selectionStart, setSelectionStart] = useState(null); // Start time in seconds
  const [selectionEnd, setSelectionEnd] = useState(null); // End time in seconds
  const [clipboard, setClipboard] = useState(null); // { audioBuffer, waveformData }
  const [editMode, setEditMode] = useState('select'); // 'select', 'trim', 'cut'
  
  // Audio Input Devices
  const [audioInputDevices, setAudioInputDevices] = useState([]);
  const [audioOutputDevices, setAudioOutputDevices] = useState([]);
  const [selectedInputDevice, setSelectedInputDevice] = useState('');
  const [selectedOutputDevice, setSelectedOutputDevice] = useState('');
  const [sampleRate, setSampleRate] = useState(48000);
  const [bufferSize, setBufferSize] = useState(512);
  const [inputChannelConfig, setInputChannelConfig] = useState({ mono: [], stereo: [] });
  const [showAudioSettings, setShowAudioSettings] = useState(false);
  
  // Available Effects
  // Available Effects - now with editable params stored per track
  const defaultEffectParams = {
    // ===== REVERBS =====
    'reverb-hall':      { decay: 4.5, wet: 0.4,  preDelay: 0.05 },
    'reverb-plate':     { decay: 2.2, wet: 0.35, preDelay: 0.02 },
    'reverb-room':      { decay: 1.0, wet: 0.25, preDelay: 0.01 },
    'reverb-jc':        { roomSize: 0.6, wet: 0.3 },
    'reverb-freeverb':  { roomSize: 0.7, dampening: 0.5, wet: 0.3 },
    // ===== DELAYS =====
    delay:              { delayTime: 0.25, feedback: 0.3, wet: 0.3 },
    'pingpong':         { delayTime: 0.25, feedback: 0.35, wet: 0.3 },
    // ===== EQ / FILTER =====
    eq:                 { low: 0, mid: 0, high: 0 },
    'filter-lp':        { frequency: 8000, Q: 1, wet: 1 },
    'filter-hp':        { frequency: 100,  Q: 1, wet: 1 },
    autowah:            { baseFrequency: 100, octaves: 6, sensitivity: 0, wet: 0.5 },
    // ===== DYNAMICS =====
    compressor:         { threshold: -24, ratio: 4, attack: 0.003, release: 0.25 },
    limiter:            { threshold: -3 },
    softener:           { threshold: -18, ratio: 2, attack: 0.05, release: 0.5 },
    // ===== DISTORTION =====
    distortion:         { distortion: 0.4, wet: 0.3 },
    bitcrusher:         { bits: 4, wet: 0.5 },
    chebyshev:          { order: 30, wet: 0.4 },
    // ===== MODULATION =====
    chorus:             { frequency: 1.5, depth: 0.7, wet: 0.3 },
    phaser:             { frequency: 0.5, octaves: 3, baseFrequency: 350, wet: 0.5 },
    tremolo:            { frequency: 5,   depth: 0.7, wet: 0.5 },
    vibrato:            { frequency: 5,   depth: 0.1, wet: 0.5 },
    autopanner:         { frequency: 1,   depth: 1,   wet: 0.5 },
    // ===== UTILITY =====
    'stereo-widener':   { width: 0.7, wet: 1 },
  };
  
  const availableEffects = [
    // REVERBS
    { id: 'reverb-hall',     name: 'Hall Reverb',     category: 'Reverbs',    color: 'blue',    params: defaultEffectParams['reverb-hall'] },
    { id: 'reverb-plate',    name: 'Plate Reverb',    category: 'Reverbs',    color: 'blue',    params: defaultEffectParams['reverb-plate'] },
    { id: 'reverb-room',     name: 'Room Reverb',     category: 'Reverbs',    color: 'blue',    params: defaultEffectParams['reverb-room'] },
    { id: 'reverb-jc',       name: 'JC Reverb',       category: 'Reverbs',    color: 'blue',    params: defaultEffectParams['reverb-jc'] },
    { id: 'reverb-freeverb', name: 'Freeverb',        category: 'Reverbs',    color: 'blue',    params: defaultEffectParams['reverb-freeverb'] },
    // DELAYS
    { id: 'delay',           name: 'Feedback Delay',  category: 'Delays',     color: 'cyan',    params: defaultEffectParams.delay },
    { id: 'pingpong',        name: 'Ping-Pong Delay', category: 'Delays',     color: 'cyan',    params: defaultEffectParams.pingpong },
    // EQ / FILTER
    { id: 'eq',              name: '3-Band EQ',       category: 'EQ / Filter',color: 'emerald', params: defaultEffectParams.eq },
    { id: 'filter-lp',       name: 'Low-Pass Filter', category: 'EQ / Filter',color: 'emerald', params: defaultEffectParams['filter-lp'] },
    { id: 'filter-hp',       name: 'High-Pass Filter',category: 'EQ / Filter',color: 'emerald', params: defaultEffectParams['filter-hp'] },
    { id: 'autowah',         name: 'Auto-Wah',        category: 'EQ / Filter',color: 'emerald', params: defaultEffectParams.autowah },
    // DYNAMICS
    { id: 'compressor',      name: 'Compressor',      category: 'Dynamics',   color: 'yellow',  params: defaultEffectParams.compressor },
    { id: 'limiter',         name: 'Limiter',         category: 'Dynamics',   color: 'yellow',  params: defaultEffectParams.limiter },
    { id: 'softener',        name: 'Softener (Glue)', category: 'Dynamics',   color: 'yellow',  params: defaultEffectParams.softener },
    // DISTORTION
    { id: 'distortion',      name: 'Distortion',      category: 'Distortion', color: 'orange',  params: defaultEffectParams.distortion },
    { id: 'bitcrusher',      name: 'Bit Crusher',     category: 'Distortion', color: 'orange',  params: defaultEffectParams.bitcrusher },
    { id: 'chebyshev',       name: 'Chebyshev',       category: 'Distortion', color: 'orange',  params: defaultEffectParams.chebyshev },
    // MODULATION
    { id: 'chorus',          name: 'Chorus',          category: 'Modulation', color: 'pink',    params: defaultEffectParams.chorus },
    { id: 'phaser',          name: 'Phaser',          category: 'Modulation', color: 'pink',    params: defaultEffectParams.phaser },
    { id: 'tremolo',         name: 'Tremolo',         category: 'Modulation', color: 'pink',    params: defaultEffectParams.tremolo },
    { id: 'vibrato',         name: 'Vibrato',         category: 'Modulation', color: 'pink',    params: defaultEffectParams.vibrato },
    { id: 'autopanner',      name: 'Auto-Panner',     category: 'Modulation', color: 'pink',    params: defaultEffectParams.autopanner },
    // UTILITY
    { id: 'stereo-widener',  name: 'Stereo Widener',  category: 'Utility',    color: 'purple',  params: defaultEffectParams['stereo-widener'] },
  ];
  
  // Track effect parameters (stores custom params per track per effect)
  const [trackEffectParams, setTrackEffectParams] = useState({}); // { trackId: { effectId: { param: value } } }
  
  // Global drag/drop state for file imports
  const [globalDragOver, setGlobalDragOver] = useState(false);
  const dragCounterRef = useRef(0);
  
  // Handle external file drops (from file explorer/desktop)
  const handleGlobalDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    dragCounterRef.current++;
    
    // Only show drop zone for files (not internal drag)
    if (e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
      setGlobalDragOver(true);
    }
    return false;
  }, []);
  
  const handleGlobalDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setGlobalDragOver(false);
    }
    return false;
  }, []);
  
  const handleGlobalDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // This is critical - prevents browser from opening the file
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
    return false;
  }, []);
  
  const handleGlobalDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setGlobalDragOver(false);
    
    if (!e.dataTransfer || !e.dataTransfer.files) {
      return false;
    }
    
    const files = Array.from(e.dataTransfer.files);
    const audioFiles = files.filter(f => 
      f.type.startsWith('audio/') || 
      f.name.match(/\.(wav|mp3|ogg|m4a|aac|flac|aiff|aif)$/i)
    );
    
    if (audioFiles.length === 0) {
      toast.error('Please drop audio files (WAV, MP3, OGG, M4A, FLAC, AIFF)');
      return false;
    }
    
    // Import each audio file
    audioFiles.forEach((file, index) => {
      // Find an empty audio track or create new one
      const audioTracks = tracks.filter(t => t.type === 'audio');
      const emptyTrack = audioTracks.find(t => !t.audioBuffer);
      
      if (emptyTrack) {
        toast.info('Audio import not yet available');
      } else {
        // Add new track and import to it
        toast.info('Audio import not yet available');
      }
      
      if (index === 0) {
        toast.success(`Importing ${audioFiles.length} audio file${audioFiles.length > 1 ? 's' : ''}...`);
      }
    });
    
    return false;
  }, [tracks]);
  const [selectedEffectForEdit, setSelectedEffectForEdit] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  
  // Audio
  const audioContextRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const analyserRef = useRef(null);
  const recordingAnalyserRef = useRef(null);
  // localStreamRef already defined above in legacy refs section
  
  // Track players and effects for playback
  const trackPlayersRef = useRef({}); // { trackId: Tone.Player }
  const trackEffectsRef = useRef({}); // { trackId: { effectId: ToneEffect } }
  const trackPannersRef = useRef({}); // { trackId: Tone.Panner }
  const trackGainsRef = useRef({}); // { trackId: Tone.Gain }
  const masterGainRef = useRef(null);
  const masterMeterRef = useRef(null);
  const inputUserMediaRef = useRef(null);
  const inputMeterRef = useRef(null);
  const meterAnimationRef = useRef(null);
  const [masterMeterLevel, setMasterMeterLevel] = useState(0);
  const [inputMeterLevel, setInputMeterLevel] = useState(0);
  const [floatingTransportVisible, setFloatingTransportVisible] = useState(() => {
    try {
      const saved = localStorage.getItem('daw_floating_transport_visible');
      return saved === null ? true : saved === 'true';
    } catch (e) { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('daw_floating_transport_visible', floatingTransportVisible ? 'true' : 'false'); } catch (e) { /* ignore */ }
  }, [floatingTransportVisible]);
  
  // Initialize master gain + master meter
  useEffect(() => {
    if (!masterGainRef.current) {
      masterGainRef.current = new Tone.Gain(masterVolume / 100).toDestination();
    }
    if (!masterMeterRef.current) {
      masterMeterRef.current = new Tone.Meter({ smoothing: 0.85 });
      masterGainRef.current.connect(masterMeterRef.current);
    }
    return () => {
      if (masterMeterRef.current) {
        try { masterMeterRef.current.dispose(); } catch (e) { console.warn('[StudioSession] master meter dispose failed:', e); }
        masterMeterRef.current = null;
      }
      if (masterGainRef.current) {
        masterGainRef.current.dispose();
        masterGainRef.current = null;
      }
    };
  }, []);
  
  // Poll meters on RAF while the page is open. Converts dBFS -> 0-100 scale.
  useEffect(() => {
    const dbToPercent = (db) => {
      if (!isFinite(db)) return 0;
      // Map -60 dB (silence) → 0, 0 dB (full scale) → 100. Clamp.
      const pct = ((db + 60) / 60) * 100;
      return Math.max(0, Math.min(100, pct));
    };
    const tick = () => {
      if (masterMeterRef.current) {
        try {
          const v = masterMeterRef.current.getValue();
          setMasterMeterLevel(dbToPercent(Array.isArray(v) ? Math.max(...v) : v));
        } catch (e) { /* meter may not be ready yet */ }
      } else {
        setMasterMeterLevel(0);
      }
      if (inputMeterRef.current) {
        try {
          const v = inputMeterRef.current.getValue();
          setInputMeterLevel(dbToPercent(Array.isArray(v) ? Math.max(...v) : v));
        } catch (e) { /* meter may not be ready yet */ }
      } else {
        setInputMeterLevel(0);
      }
      meterAnimationRef.current = requestAnimationFrame(tick);
    };
    meterAnimationRef.current = requestAnimationFrame(tick);
    return () => {
      if (meterAnimationRef.current) cancelAnimationFrame(meterAnimationRef.current);
    };
  }, []);
  
  // Open/close the live input monitor whenever the selected input device changes
  useEffect(() => {
    const closeInput = () => {
      if (inputUserMediaRef.current) {
        try { inputUserMediaRef.current.close(); inputUserMediaRef.current.dispose(); } catch (e) { /* safe */ }
        inputUserMediaRef.current = null;
      }
      if (inputMeterRef.current) {
        try { inputMeterRef.current.dispose(); } catch (e) { /* safe */ }
        inputMeterRef.current = null;
      }
    };
    closeInput();
    if (!selectedInputDevice) return;
    let cancelled = false;
    (async () => {
      try {
        const mic = new Tone.UserMedia();
        await mic.open(selectedInputDevice);
        if (cancelled) { try { mic.close(); mic.dispose(); } catch (e) { /* safe */ } return; }
        const meter = new Tone.Meter({ smoothing: 0.85 });
        mic.connect(meter);
        inputUserMediaRef.current = mic;
        inputMeterRef.current = meter;
      } catch (err) {
        console.warn('[StudioSession] could not open input device for metering:', err);
      }
    })();
    return () => { cancelled = true; closeInput(); };
  }, [selectedInputDevice]);
  
  // Route master audio to the chosen physical output device via setSinkId
  // (supported by Chrome 110+ and modern Edge; silently ignored elsewhere).
  useEffect(() => {
    if (!selectedOutputDevice) return;
    const ctx = Tone.getContext()?.rawContext;
    if (ctx && typeof ctx.setSinkId === 'function') {
      ctx.setSinkId(selectedOutputDevice).catch((err) => {
        console.warn('[StudioSession] setSinkId failed:', err);
        toast.error('Could not switch audio output — device may be in use');
      });
    }
  }, [selectedOutputDevice]);
  
  // Update master volume when it changes
  useEffect(() => {
    if (masterGainRef.current) {
      masterGainRef.current.gain.value = masterVolume / 100;
    }
  }, [masterVolume]);
  
  // ============================================
  // GLOBAL DRAG/DROP PREVENTION
  // ============================================
  // Prevent browser from opening dropped files - especially important for Safari
  useEffect(() => {
    const preventDefaults = (e) => {
      e.preventDefault();
      e.stopPropagation();
      // Explicitly set dropEffect to prevent browser default behavior
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'none';
      }
      return false;
    };
    
    // Prevent browser from opening files dropped anywhere on the page
    // Add to both window AND document for cross-browser compatibility (Safari needs document)
    const events = ['dragenter', 'dragover', 'dragleave', 'drop'];
    
    events.forEach(eventName => {
      document.addEventListener(eventName, preventDefaults, { capture: true });
      document.body.addEventListener(eventName, preventDefaults, { capture: true });
    });
    
    return () => {
      events.forEach(eventName => {
        document.removeEventListener(eventName, preventDefaults, { capture: true });
        document.body.removeEventListener(eventName, preventDefaults, { capture: true });
      });
    };
  }, []);
  
  // ============================================
  // KEYBOARD SHORTCUTS
  // ============================================
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if user is typing in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }
      
      switch (e.code) {
        case 'Space':
          e.preventDefault();
          // Toggle play/pause
          document.querySelector('[data-testid="transport-play"]')?.click();
          break;
        case 'KeyR':
          e.preventDefault();
          // Toggle record
          document.querySelector('[data-testid="transport-record"]')?.click();
          break;
        case 'KeyS':
          if (e.metaKey || e.ctrlKey) {
            e.preventDefault();
            // Save project
            document.querySelector('[data-testid="save-project"]')?.click();
          }
          break;
        case 'KeyZ':
          if (e.metaKey || e.ctrlKey) {
            e.preventDefault();
            // Undo
            document.querySelector('[data-testid="undo-btn"]')?.click();
          }
          break;
        case 'KeyY':
          if (e.metaKey || e.ctrlKey) {
            e.preventDefault();
            // Redo
            document.querySelector('[data-testid="redo-btn"]')?.click();
          }
          break;
        case 'Home':
          e.preventDefault();
          // Go to start
          setCurrentBeat(0);
          break;
        case 'End':
          e.preventDefault();
          // Go to end (last beat with content)
          const maxBeat = Math.max(...tracks.map(t => t.audioBuffer ? t.audioBuffer.duration * tempo / 60 : 0), 16);
          setCurrentBeat(maxBeat);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          // Move playhead back 1 beat (or 4 beats with Shift)
          setCurrentBeat(prev => Math.max(0, prev - (e.shiftKey ? 4 : 1)));
          break;
        case 'ArrowRight':
          e.preventDefault();
          // Move playhead forward 1 beat (or 4 beats with Shift)
          setCurrentBeat(prev => prev + (e.shiftKey ? 4 : 1));
          break;
        case 'KeyM':
          // Mute selected track
          if (selectedTrackId) {
            toggleMute(selectedTrackId);
          }
          break;
        case 'Delete':
        case 'Backspace':
          // Delete selected track
          if (selectedTrackId && !e.metaKey && !e.ctrlKey) {
            e.preventDefault();
            deleteTrack(selectedTrackId);
          }
          break;
        case 'Digit1':
        case 'Digit2':
        case 'Digit3':
        case 'Digit4':
        case 'Digit5':
        case 'Digit6':
        case 'Digit7':
        case 'Digit8':
        case 'Digit9':
          // Select track by number
          const trackIndex = parseInt(e.code.replace('Digit', '')) - 1;
          if (tracks[trackIndex]) {
            setSelectedTrackId(tracks[trackIndex].id);
          }
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedTrackId, tracks, tempo]);
  
  // Animation
  const animationRef = useRef(null);
  const startTimeRef = useRef(0);
  const playheadOffsetRef = useRef(0);
  
  const pixelsPerBeat = 30;
  
  // trackColors already defined above with initial tracks

  // Close cursor menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showCursorMenu && !e.target.closest('[data-cursor-menu]')) {
        setShowCursorMenu(false);
      }
      if (showQuantizeMenu && !e.target.closest('[data-quantize-menu]')) {
        setShowQuantizeMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showCursorMenu, showQuantizeMenu]);

  // Enumerate audio input devices
  useEffect(() => {
    const getDevices = async () => {
      try {
        // Request permission first
        await navigator.mediaDevices.getUserMedia({ audio: true });
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(d => d.kind === 'audioinput');
        const audioOutputs = devices.filter(d => d.kind === 'audiooutput');
        setAudioInputDevices(audioInputs);
        setAudioOutputDevices(audioOutputs);
        if (audioInputs.length > 0 && !selectedInputDevice) {
          setSelectedInputDevice(audioInputs[0].deviceId);
        }
        if (audioOutputs.length > 0 && !selectedOutputDevice) {
          setSelectedOutputDevice(audioOutputs[0].deviceId);
        }
      } catch (err) {
        console.log('Could not enumerate devices:', err);
      }
    };
    getDevices();
    
    // Listen for device changes
    navigator.mediaDevices.addEventListener('devicechange', getDevices);
    return () => navigator.mediaDevices.removeEventListener('devicechange', getDevices);
  }, []);

  // Initialize metronome
  useEffect(() => {
    if (!metronomeRef.current) {
      metronomeRef.current = new Tone.MembraneSynth({
        pitchDecay: 0.008,
        octaves: 2,
        envelope: { attack: 0.001, decay: 0.3, sustain: 0, release: 0.1 }
      }).toDestination();
    }
    metronomeRef.current.volume.value = Tone.gainToDb(metronomeVolume / 100);
  }, [metronomeVolume]);

  // Schedule metronome clicks
  useEffect(() => {
    if (metronomeEnabled && isPlaying) {
      Tone.Transport.scheduleRepeat((time) => {
        if (metronomeRef.current) {
          const beat = Math.floor(Tone.Transport.position.split(':')[1]);
          const note = beat === 0 ? 'C3' : 'C2';
          metronomeRef.current.triggerAttackRelease(note, '16n', time);
        }
      }, '4n');
    }
    return () => {
      Tone.Transport.cancel();
    };
  }, [metronomeEnabled, isPlaying]);

  // Initialize
  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    Tone.Transport.bpm.value = tempo;
    
    return () => {
      if (audioContextRef.current) audioContextRef.current.close();
      cancelAnimationFrame(animationRef.current);
    };
  }, []);

  // Update tempo
  useEffect(() => {
    Tone.Transport.bpm.value = tempo;
  }, [tempo]);

  // Auto-start session if roomCode is provided (coming from Remote Session)
  useEffect(() => {
    if (roomCode) {
      setLiveKitRoomCode(roomCode.toUpperCase());
    }
  }, [roomCode]);

  // Copy client link to clipboard
  const copyClientLink = () => {
    const link = `${window.location.origin}/livekit-room/${liveKitRoomCode}`;
    navigator.clipboard.writeText(link);
    toast.success('Client link copied! Send this to your client.');
  };

  // Add track
  const addTrack = (type = 'audio') => {
    const newTrack = {
      id: `track_${Date.now()}`,
      name: `${type === 'audio' ? 'Audio' : 'MIDI'} ${tracks.length + 1}`,
      type,
      color: trackColors[tracks.length % trackColors.length],
      volume: 80,
      pan: 0,
      muted: false,
      solo: false,
      armed: false,
      waveformData: null,
      audioBuffer: null,
      notes: [],
      inputDevice: selectedInputDevice || '',
      inputSource: 'Input 1',
      instrument: 'Piano',
      effects: [] // Array of effect IDs
    };
    
    setTracks(prev => [...prev, newTrack]);
    setSelectedTrackId(newTrack.id);
    toast.success(`Added ${type} track`);
  };

  // Change track input channel
  const changeInputDevice = (trackId, channelId) => {
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { ...t, inputChannel: channelId } : t
    ));
    // Format the display name
    let displayName = channelId;
    if (channelId.startsWith('mono_')) {
      const num = channelId.replace('mono_', '');
      displayName = `Input ${num}`;
    } else if (channelId.startsWith('stereo_')) {
      const nums = channelId.replace('stereo_', '').split('_');
      displayName = `Input ${nums[0]}/${nums[1]}`;
    }
    toast.success(`Input: ${displayName}`);
  };

  // Open FX panel for track
  const openFxPanel = (trackId) => {
    setSelectedTrackForFx(trackId);
    setShowFxPanel(true);
  };

  // Add effect to track
  const addEffectToTrack = (trackId, effectId) => {
    setTracks(prev => prev.map(t => {
      if (t.id === trackId && !t.effects.includes(effectId)) {
        return { ...t, effects: [...t.effects, effectId] };
      }
      return t;
    }));
    const effect = availableEffects.find(e => e.id === effectId);
    toast.success(`Added ${effect?.name} to track`);
  };

  // Remove effect from track
  const removeEffectFromTrack = (trackId, effectId) => {
    setTracks(prev => prev.map(t => {
      if (t.id === trackId) {
        return { ...t, effects: t.effects.filter(e => e !== effectId) };
      }
      return t;
    }));
    // Also remove params
    setTrackEffectParams(prev => {
      const newParams = { ...prev };
      if (newParams[trackId]) {
        delete newParams[trackId][effectId];
      }
      return newParams;
    });
    if (selectedEffectForEdit === effectId) {
      setSelectedEffectForEdit(null);
    }
  };

  // Reorder effects via drag & drop
  const [draggingEffect, setDraggingEffect] = useState(null);
  const [dragOverEffect, setDragOverEffect] = useState(null);
  
  const reorderEffects = (trackId, fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    setTracks(prev => prev.map(t => {
      if (t.id === trackId) {
        const newEffects = [...t.effects];
        const [removed] = newEffects.splice(fromIndex, 1);
        newEffects.splice(toIndex, 0, removed);
        return { ...t, effects: newEffects };
      }
      return t;
    }));
    toast.success('Effect order updated');
  };

  // Get effect params for a specific track's effect (with defaults)
  const getEffectParams = (trackId, effectId) => {
    return trackEffectParams[trackId]?.[effectId] || defaultEffectParams[effectId] || {};
  };

  // Update effect parameter for a track
  const updateEffectParam = (trackId, effectId, param, value) => {
    setTrackEffectParams(prev => ({
      ...prev,
      [trackId]: {
        ...(prev[trackId] || {}),
        [effectId]: {
          ...(prev[trackId]?.[effectId] || defaultEffectParams[effectId] || {}),
          [param]: value
        }
      }
    }));
  };

  // Change track pan
  const changePan = (trackId, pan) => {
    setTracks(prev => prev.map(t => {
      if (t.id === trackId) {
        // Apply pan to Tone.js panner if exists
        if (trackPannersRef.current?.[trackId]) {
          trackPannersRef.current[trackId].pan.value = pan;
        }
        return { ...t, pan };
      }
      return t;
    }));
  };

  // Change EQ settings for a track
  const changeEQ = (trackId, eq) => {
    setTracks(prev => prev.map(t => {
      if (t.id === trackId) {
        // TODO: Apply EQ to Tone.js EQ3 node if connected
        return { ...t, eq };
      }
      return t;
    }));
  };

  // Move audio clip to a new beat position
  const moveAudioClip = (trackId, newBeat) => {
    setTracks(prev => prev.map(t => {
      if (t.id === trackId) {
        return { ...t, audioStartBeat: Math.max(0, newBeat) };
      }
      return t;
    }));
  };

  // Reorder track to a new position
  const reorderTrack = (trackId, newIndex) => {
    setTracks(prev => {
      const trackIndex = prev.findIndex(t => t.id === trackId);
      if (trackIndex === -1 || trackIndex === newIndex) return prev;
      
      const newTracks = [...prev];
      const [removed] = newTracks.splice(trackIndex, 1);
      newTracks.splice(newIndex, 0, removed);
      return newTracks;
    });
  };

  // Move audio from one track to another
  const moveAudioToTrack = (sourceTrackId, targetTrackId, beatPosition = 0) => {
    setTracks(prev => {
      const sourceTrack = prev.find(t => t.id === sourceTrackId);
      if (!sourceTrack || !sourceTrack.waveformData) return prev;
      
      // Copy audio data to target track
      return prev.map(t => {
        if (t.id === sourceTrackId) {
          // Clear source track
          return { ...t, waveformData: null, audioBuffer: null, audioStartBeat: 0 };
        }
        if (t.id === targetTrackId && t.type === 'audio') {
          // Move audio to target track
          return { 
            ...t, 
            waveformData: sourceTrack.waveformData, 
            audioBuffer: sourceTrack.audioBuffer,
            audioStartBeat: beatPosition
          };
        }
        return t;
      });
    });
    toast.success('Audio moved to new track');
  };

  // Follow playhead toggle
  const [followPlayhead, setFollowPlayhead] = useState(true);

  // Auto-scroll to follow playhead during playback
  useEffect(() => {
    if (followPlayhead && isPlaying) {
      const container = document.getElementById('tracks-scroll-container');
      if (container) {
        const playheadPosition = 208 + currentBeat * pixelsPerBeat * zoom;
        const containerWidth = container.clientWidth;
        const scrollLeft = container.scrollLeft;
        
        // If playhead is near the right edge (80% of visible area), scroll
        const visibleRight = scrollLeft + containerWidth * 0.8;
        if (playheadPosition > visibleRight) {
          container.scrollTo({
            left: playheadPosition - containerWidth * 0.3,
            behavior: 'smooth'
          });
        }
        // If playhead is before visible area, scroll back
        else if (playheadPosition < scrollLeft + 200) {
          container.scrollTo({
            left: Math.max(0, playheadPosition - 200),
            behavior: 'smooth'
          });
        }
      }
    }
  }, [currentBeat, followPlayhead, isPlaying, pixelsPerBeat, zoom]);

  // Simulated meter levels (in real implementation, connect to Tone.js analyzer)
  const [meterLevels, setMeterLevels] = useState({});
  
  // Simulate meter animation when playing
  useEffect(() => {
    if (isPlaying) {
      const interval = setInterval(() => {
        setMeterLevels(prev => {
          const newLevels = {};
          tracks.forEach(track => {
            if (!track.muted) {
              // Simulate based on volume with some randomness
              const base = track.volume * 0.7;
              newLevels[track.id] = {
                level: Math.min(100, base + Math.random() * 30),
                peak: Math.min(100, base + Math.random() * 40)
              };
            } else {
              newLevels[track.id] = { level: 0, peak: 0 };
            }
          });
          return newLevels;
        });
      }, 100);
      return () => clearInterval(interval);
    } else {
      setMeterLevels({});
    }
  }, [isPlaying, tracks]);

  // ============================================
  // WAVEFORM EDITING FUNCTIONS
  // ============================================
  
  // Set selection on waveform (called from waveform click/drag)
  const setWaveformSelection = (trackId, startSec, endSec) => {
    setSelectionStart(Math.min(startSec, endSec));
    setSelectionEnd(Math.max(startSec, endSec));
  };

  // Clear selection
  const clearSelection = () => {
    setSelectionStart(null);
    setSelectionEnd(null);
  };

  // Copy selected region to clipboard
  const copySelection = () => {
    if (!selectedTrackId || selectionStart === null || selectionEnd === null) {
      toast.error('No selection to copy');
      return;
    }
    
    const track = tracks.find(t => t.id === selectedTrackId);
    if (!track?.audioBuffer) {
      toast.error('No audio to copy');
      return;
    }
    
    const sampleRate = track.audioBuffer.sampleRate;
    const startSample = Math.floor(selectionStart * sampleRate);
    const endSample = Math.floor(selectionEnd * sampleRate);
    const length = endSample - startSample;
    
    if (length <= 0) {
      toast.error('Invalid selection');
      return;
    }
    
    // Create new buffer with selection
    const numChannels = track.audioBuffer.numberOfChannels;
    const newBuffer = audioContextRef.current.createBuffer(numChannels, length, sampleRate);
    
    for (let ch = 0; ch < numChannels; ch++) {
      const channelData = track.audioBuffer.getChannelData(ch);
      const newChannelData = newBuffer.getChannelData(ch);
      for (let i = 0; i < length; i++) {
        newChannelData[i] = channelData[startSample + i];
      }
    }
    
    // Generate waveform data for clipboard
    const rawData = newBuffer.getChannelData(0);
    const samples = Math.min(2000, length);
    const blockSize = Math.floor(length / samples);
    const waveformData = [];
    
    for (let i = 0; i < samples; i++) {
      let sum = 0;
      for (let j = 0; j < blockSize; j++) {
        sum += Math.abs(rawData[i * blockSize + j] || 0);
      }
      waveformData.push(sum / blockSize);
    }
    const max = Math.max(...waveformData);
    const normalized = waveformData.map(v => max > 0 ? v / max : 0);
    
    setClipboard({ audioBuffer: newBuffer, waveformData: normalized });
    toast.success(`Copied ${(selectionEnd - selectionStart).toFixed(2)}s to clipboard`);
  };

  // Cut selected region (copy + delete)
  const cutSelection = () => {
    if (!selectedTrackId || selectionStart === null || selectionEnd === null) {
      toast.error('No selection to cut');
      return;
    }
    
    // First copy
    copySelection();
    
    // Then delete the selection
    deleteSelection();
  };

  // Delete selected region
  const deleteSelection = () => {
    if (!selectedTrackId || selectionStart === null || selectionEnd === null) {
      toast.error('No selection to delete');
      return;
    }
    
    const track = tracks.find(t => t.id === selectedTrackId);
    if (!track?.audioBuffer) return;
    
    const sampleRate = track.audioBuffer.sampleRate;
    const startSample = Math.floor(selectionStart * sampleRate);
    const endSample = Math.floor(selectionEnd * sampleRate);
    const totalLength = track.audioBuffer.length;
    const newLength = totalLength - (endSample - startSample);
    
    if (newLength <= 0) {
      // Deleting entire track audio
      setTracks(prev => prev.map(t => 
        t.id === selectedTrackId ? { ...t, audioBuffer: null, waveformData: null } : t
      ));
      clearSelection();
      toast.success('Audio deleted');
      return;
    }
    
    // Create new buffer without selection
    const numChannels = track.audioBuffer.numberOfChannels;
    const newBuffer = audioContextRef.current.createBuffer(numChannels, newLength, sampleRate);
    
    for (let ch = 0; ch < numChannels; ch++) {
      const channelData = track.audioBuffer.getChannelData(ch);
      const newChannelData = newBuffer.getChannelData(ch);
      
      // Copy before selection
      for (let i = 0; i < startSample; i++) {
        newChannelData[i] = channelData[i];
      }
      // Copy after selection
      for (let i = endSample; i < totalLength; i++) {
        newChannelData[i - (endSample - startSample)] = channelData[i];
      }
    }
    
    // Update waveform
    const rawData = newBuffer.getChannelData(0);
    const samples = 2000;
    const blockSize = Math.floor(newLength / samples);
    const waveformData = [];
    
    for (let i = 0; i < samples; i++) {
      let sum = 0;
      for (let j = 0; j < blockSize; j++) {
        sum += Math.abs(rawData[i * blockSize + j] || 0);
      }
      waveformData.push(sum / blockSize);
    }
    const max = Math.max(...waveformData);
    const normalized = waveformData.map(v => max > 0 ? v / max : 0);
    
    setTracks(prev => prev.map(t => 
      t.id === selectedTrackId ? { ...t, audioBuffer: newBuffer, waveformData: normalized } : t
    ));
    clearSelection();
    toast.success('Selection deleted');
  };

  // Paste clipboard at current position
  const pasteClipboard = () => {
    if (!selectedTrackId || !clipboard?.audioBuffer) {
      toast.error('Nothing to paste');
      return;
    }
    
    const track = tracks.find(t => t.id === selectedTrackId);
    if (!track) return;
    
    const pastePosition = selectionStart !== null ? selectionStart : currentBeat * 60 / tempo;
    const clipboardBuffer = clipboard.audioBuffer;
    const clipboardSampleRate = clipboardBuffer.sampleRate;
    
    if (!track.audioBuffer) {
      // Paste into empty track
      setTracks(prev => prev.map(t => 
        t.id === selectedTrackId ? { ...t, audioBuffer: clipboardBuffer, waveformData: clipboard.waveformData } : t
      ));
      toast.success('Pasted audio');
      return;
    }
    
    // Insert clipboard into existing audio
    const trackBuffer = track.audioBuffer;
    const sampleRate = trackBuffer.sampleRate;
    const insertSample = Math.floor(pastePosition * sampleRate);
    const clipLength = clipboardBuffer.length;
    const newLength = trackBuffer.length + clipLength;
    
    const numChannels = Math.max(trackBuffer.numberOfChannels, clipboardBuffer.numberOfChannels);
    const newBuffer = audioContextRef.current.createBuffer(numChannels, newLength, sampleRate);
    
    for (let ch = 0; ch < numChannels; ch++) {
      const trackData = ch < trackBuffer.numberOfChannels ? trackBuffer.getChannelData(ch) : new Float32Array(trackBuffer.length);
      const clipData = ch < clipboardBuffer.numberOfChannels ? clipboardBuffer.getChannelData(ch) : new Float32Array(clipLength);
      const newData = newBuffer.getChannelData(ch);
      
      // Copy before insert point
      for (let i = 0; i < insertSample && i < trackBuffer.length; i++) {
        newData[i] = trackData[i];
      }
      // Copy clipboard
      for (let i = 0; i < clipLength; i++) {
        newData[insertSample + i] = clipData[i];
      }
      // Copy after insert point
      for (let i = insertSample; i < trackBuffer.length; i++) {
        newData[clipLength + i] = trackData[i];
      }
    }
    
    // Update waveform
    const rawData = newBuffer.getChannelData(0);
    const samples = 2000;
    const blockSize = Math.floor(newLength / samples);
    const waveformData = [];
    
    for (let i = 0; i < samples; i++) {
      let sum = 0;
      for (let j = 0; j < blockSize; j++) {
        sum += Math.abs(rawData[i * blockSize + j] || 0);
      }
      waveformData.push(sum / blockSize);
    }
    const max = Math.max(...waveformData);
    const normalized = waveformData.map(v => max > 0 ? v / max : 0);
    
    setTracks(prev => prev.map(t => 
      t.id === selectedTrackId ? { ...t, audioBuffer: newBuffer, waveformData: normalized } : t
    ));
    toast.success('Pasted audio');
  };

  // Trim to selection (delete everything outside selection)
  const trimToSelection = () => {
    if (!selectedTrackId || selectionStart === null || selectionEnd === null) {
      toast.error('No selection to trim to');
      return;
    }
    
    const track = tracks.find(t => t.id === selectedTrackId);
    if (!track?.audioBuffer) return;
    
    const sampleRate = track.audioBuffer.sampleRate;
    const startSample = Math.floor(selectionStart * sampleRate);
    const endSample = Math.floor(selectionEnd * sampleRate);
    const length = endSample - startSample;
    
    if (length <= 0) return;
    
    const numChannels = track.audioBuffer.numberOfChannels;
    const newBuffer = audioContextRef.current.createBuffer(numChannels, length, sampleRate);
    
    for (let ch = 0; ch < numChannels; ch++) {
      const channelData = track.audioBuffer.getChannelData(ch);
      const newChannelData = newBuffer.getChannelData(ch);
      for (let i = 0; i < length; i++) {
        newChannelData[i] = channelData[startSample + i];
      }
    }
    
    // Update waveform
    const rawData = newBuffer.getChannelData(0);
    const samples = 2000;
    const blockSize = Math.floor(length / samples);
    const waveformData = [];
    
    for (let i = 0; i < samples; i++) {
      let sum = 0;
      for (let j = 0; j < blockSize; j++) {
        sum += Math.abs(rawData[i * blockSize + j] || 0);
      }
      waveformData.push(sum / blockSize);
    }
    const max = Math.max(...waveformData);
    const normalized = waveformData.map(v => max > 0 ? v / max : 0);
    
    setTracks(prev => prev.map(t => 
      t.id === selectedTrackId ? { ...t, audioBuffer: newBuffer, waveformData: normalized } : t
    ));
    clearSelection();
    toast.success('Trimmed to selection');
  };

  // Create Tone.js effect instance from effect ID with custom params
  const createToneEffect = (effectId, params) => {
    const effectParams = params || defaultEffectParams[effectId];
    if (!effectParams) return null;
    
    switch (effectId) {
      // ===== REVERBS =====
      case 'reverb-hall':
      case 'reverb-plate':
      case 'reverb-room':
        return new Tone.Reverb({
          decay: effectParams.decay,
          preDelay: effectParams.preDelay ?? 0.01,
          wet: effectParams.wet,
        });
      case 'reverb-jc':
        return new Tone.JCReverb({
          roomSize: effectParams.roomSize,
          wet: effectParams.wet,
        });
      case 'reverb-freeverb':
        return new Tone.Freeverb({
          roomSize: effectParams.roomSize,
          dampening: effectParams.dampening * 6000 + 500, // 0-1 → 500-6500 Hz
          wet: effectParams.wet,
        });
      // ===== DELAYS =====
      case 'delay':
        return new Tone.FeedbackDelay({
          delayTime: effectParams.delayTime,
          feedback: effectParams.feedback,
          wet: effectParams.wet,
        });
      case 'pingpong':
        return new Tone.PingPongDelay({
          delayTime: effectParams.delayTime,
          feedback: effectParams.feedback,
          wet: effectParams.wet,
        });
      // ===== EQ / FILTER =====
      case 'eq':
        return new Tone.EQ3({
          low: effectParams.low,
          mid: effectParams.mid,
          high: effectParams.high,
        });
      case 'filter-lp':
        return new Tone.Filter({
          frequency: effectParams.frequency,
          type: 'lowpass',
          Q: effectParams.Q,
        });
      case 'filter-hp':
        return new Tone.Filter({
          frequency: effectParams.frequency,
          type: 'highpass',
          Q: effectParams.Q,
        });
      case 'autowah':
        return new Tone.AutoWah({
          baseFrequency: effectParams.baseFrequency,
          octaves: effectParams.octaves,
          sensitivity: effectParams.sensitivity,
          wet: effectParams.wet,
        });
      // ===== DYNAMICS =====
      case 'compressor':
      case 'softener':
        return new Tone.Compressor({
          threshold: effectParams.threshold,
          ratio: effectParams.ratio,
          attack: effectParams.attack,
          release: effectParams.release,
        });
      case 'limiter':
        return new Tone.Limiter(effectParams.threshold);
      // ===== DISTORTION =====
      case 'distortion':
        return new Tone.Distortion({
          distortion: effectParams.distortion,
          wet: effectParams.wet,
        });
      case 'bitcrusher':
        return new Tone.BitCrusher({
          bits: Math.max(1, Math.round(effectParams.bits)),
          wet: effectParams.wet,
        });
      case 'chebyshev':
        return new Tone.Chebyshev({
          order: Math.max(1, Math.round(effectParams.order)),
          wet: effectParams.wet,
        });
      // ===== MODULATION =====
      case 'chorus':
        return new Tone.Chorus({
          frequency: effectParams.frequency,
          depth: effectParams.depth,
          wet: effectParams.wet,
        }).start();
      case 'phaser':
        return new Tone.Phaser({
          frequency: effectParams.frequency,
          octaves: effectParams.octaves,
          baseFrequency: effectParams.baseFrequency,
          wet: effectParams.wet,
        });
      case 'tremolo':
        return new Tone.Tremolo({
          frequency: effectParams.frequency,
          depth: effectParams.depth,
          wet: effectParams.wet,
        }).start();
      case 'vibrato':
        return new Tone.Vibrato({
          frequency: effectParams.frequency,
          depth: effectParams.depth,
          wet: effectParams.wet,
        });
      case 'autopanner':
        return new Tone.AutoPanner({
          frequency: effectParams.frequency,
          depth: effectParams.depth,
          wet: effectParams.wet,
        }).start();
      // ===== UTILITY =====
      case 'stereo-widener':
        return new Tone.StereoWidener({
          width: effectParams.width,
          wet: effectParams.wet,
        });
      default:
        return null;
    }
  };

  // Build effect chain for a track and connect to destination with volume and pan
  const buildTrackEffectChain = (track, player) => {
    // Dispose of old effects for this track
    if (trackEffectsRef.current[track.id]) {
      Object.values(trackEffectsRef.current[track.id]).forEach(effect => {
        try { effect.dispose(); } catch (e) { console.warn('[StudioSession] effect dispose failed (safe to ignore):', e); }
      });
    }
    trackEffectsRef.current[track.id] = {};
    
    // Dispose of old panner and gain
    if (trackPannersRef.current[track.id]) {
      try { trackPannersRef.current[track.id].dispose(); } catch (e) { console.warn('[StudioSession] panner dispose failed (safe to ignore):', e); }
    }
    if (trackGainsRef.current[track.id]) {
      try { trackGainsRef.current[track.id].dispose(); } catch (e) { console.warn('[StudioSession] gain dispose failed (safe to ignore):', e); }
    }
    
    // Create new effects with custom params
    const effectInstances = [];
    for (const effectId of track.effects) {
      const params = getEffectParams(track.id, effectId);
      const effect = createToneEffect(effectId, params);
      if (effect) {
        trackEffectsRef.current[track.id][effectId] = effect;
        effectInstances.push(effect);
      }
    }
    
    // Create panner for pan control (-1 to 1)
    const panner = new Tone.Panner(track.pan || 0);
    trackPannersRef.current[track.id] = panner; // Store reference for live control
    
    // Create gain node for volume control
    const gain = new Tone.Gain(track.volume / 100);
    trackGainsRef.current[track.id] = gain; // Store reference for live control
    
    // Ensure master gain exists
    if (!masterGainRef.current) {
      masterGainRef.current = new Tone.Gain(masterVolume / 100).toDestination();
    }
    
    // Build chain: player -> effects -> panner -> gain -> MASTER GAIN -> destination
    if (effectInstances.length > 0) {
      player.disconnect();
      player.chain(...effectInstances, panner, gain, masterGainRef.current);
    } else {
      player.disconnect();
      player.connect(panner);
      panner.connect(gain);
      gain.connect(masterGainRef.current);
    }
    
    return { gain, panner };
  };

  // Stop all track players
  const stopAllPlayers = () => {
    Object.values(trackPlayersRef.current).forEach(player => {
      try {
        player.stop();
        player.dispose();
      } catch (e) { console.warn('[StudioSession] player cleanup failed (safe to ignore):', e); }
    });
    trackPlayersRef.current = {};
    
    // Dispose effects
    Object.values(trackEffectsRef.current).forEach(trackEffects => {
      Object.values(trackEffects).forEach(effect => {
        try { effect.dispose(); } catch (e) { console.warn('[StudioSession] effect dispose failed (safe to ignore):', e); }
      });
    });
    trackEffectsRef.current = {};
  };

  // Export session to audio file
  const exportSession = async () => {
    const tracksWithAudio = tracks.filter(t => t.audioBuffer && !t.muted);
    const soloTracks = tracksWithAudio.filter(t => t.solo);
    const tracksToExport = soloTracks.length > 0 ? soloTracks : tracksWithAudio;
    
    if (tracksToExport.length === 0) {
      toast.error('No audio tracks to export');
      return;
    }
    
    setIsExporting(true);
    toast.info('Exporting session...');
    
    try {
      // Find the longest track duration
      const maxDuration = Math.max(...tracksToExport.map(t => t.audioBuffer.duration));
      
      // Use Tone.Offline to render the mix
      const renderedBuffer = await Tone.Offline(({ transport }) => {
        // Create master gain
        const masterGain = new Tone.Gain(masterVolume / 100).toDestination();
        
        for (const track of tracksToExport) {
          // Create buffer and player
          const toneBuffer = new Tone.ToneAudioBuffer(track.audioBuffer);
          const player = new Tone.Player(toneBuffer);
          
          // Create effect chain
          const effectInstances = [];
          for (const effectId of track.effects) {
            const params = getEffectParams(track.id, effectId);
            const effect = createToneEffect(effectId, params);
            if (effect) {
              effectInstances.push(effect);
            }
          }
          
          // Create panner and gain
          const panner = new Tone.Panner(track.pan || 0);
          const gain = new Tone.Gain(track.volume / 100);
          
          // Wire up the chain
          if (effectInstances.length > 0) {
            player.chain(...effectInstances, panner, gain, masterGain);
          } else {
            player.connect(panner);
            panner.connect(gain);
            gain.connect(masterGain);
          }
          
          // Start player
          player.start(0);
        }
        
        transport.start(0);
      }, maxDuration, 2, sampleRate);
      
      // Convert to WAV
      const wavBlob = await bufferToWav(renderedBuffer);
      
      // Download
      const url = URL.createObjectURL(wavBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `session_export_${Date.now()}.wav`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Session exported successfully!');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Export failed: ' + err.message);
    } finally {
      setIsExporting(false);
    }
  };
  
  // Convert AudioBuffer to WAV blob
  const bufferToWav = async (buffer) => {
    const numChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;
    
    const bytesPerSample = bitDepth / 8;
    const blockAlign = numChannels * bytesPerSample;
    
    const samples = buffer.length;
    const dataSize = samples * blockAlign;
    const fileSize = 44 + dataSize;
    
    const arrayBuffer = new ArrayBuffer(fileSize);
    const view = new DataView(arrayBuffer);
    
    // Write WAV header
    const writeString = (offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };
    
    writeString(0, 'RIFF');
    view.setUint32(4, fileSize - 8, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true); // fmt chunk size
    view.setUint16(20, format, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    writeString(36, 'data');
    view.setUint32(40, dataSize, true);
    
    // Interleave channels and write samples
    const offset = 44;
    const channelData = [];
    for (let c = 0; c < numChannels; c++) {
      channelData.push(buffer.getChannelData(c));
    }
    
    for (let i = 0; i < samples; i++) {
      for (let c = 0; c < numChannels; c++) {
        const sample = Math.max(-1, Math.min(1, channelData[c][i]));
        const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
        view.setInt16(offset + (i * numChannels + c) * bytesPerSample, intSample, true);
      }
    }
    
    return new Blob([arrayBuffer], { type: 'audio/wav' });
  };

  // Import audio from file (via modal) - accepts File object directly
  const importAudioFromFile = async (file, targetTrackId = null, startBeat = 0) => {
    if (!file) return;
    
    try {
      toast.info(`Loading ${file.name}...`);
      const arrayBuffer = await file.arrayBuffer();
      const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
      
      // Extract waveform data
      const rawData = audioBuffer.getChannelData(0);
      const samples = 2000;
      const blockSize = Math.floor(rawData.length / samples);
      const waveformData = [];
      
      for (let i = 0; i < samples; i++) {
        let sum = 0;
        for (let j = 0; j < blockSize; j++) {
          sum += Math.abs(rawData[i * blockSize + j] || 0);
        }
        waveformData.push(sum / blockSize);
      }
      
      const max = Math.max(...waveformData);
      const normalized = waveformData.map(v => v / max);
      
      // If target track specified and it's an audio track, add to that track
      if (targetTrackId) {
        const targetTrack = tracks.find(t => t.id === targetTrackId);
        if (targetTrack && targetTrack.type === 'audio') {
          setTracks(prev => prev.map(t => {
            if (t.id === targetTrackId) {
              return {
                ...t,
                waveformData: normalized,
                audioBuffer: audioBuffer,
                audioStartBeat: startBeat,
                inputSource: 'File: ' + file.name
              };
            }
            return t;
          }));
          toast.success(`Added ${file.name} to ${targetTrack.name} at beat ${startBeat}`);
          return;
        }
      }
      
      // Otherwise create new track with imported audio
      const newTrack = {
        id: `track_${Date.now()}`,
        name: file.name.replace(/\.[^/.]+$/, ''),
        type: 'audio',
        color: trackColors[tracks.length % trackColors.length],
        volume: 80,
        pan: 0,
        muted: false,
        solo: false,
        armed: false,
        waveformData: normalized,
        audioBuffer: audioBuffer,
        audioStartBeat: startBeat,
        notes: [],
        inputDevice: '',
        inputSource: 'File: ' + file.name,
        instrument: null,
        effects: [],
        eq: { low: 0, mid: 0, high: 0 }
      };
      
      setTracks(prev => [...prev, newTrack]);
      setSelectedTrackId(newTrack.id);
      toast.success(`Imported: ${file.name}`);
    } catch (err) {
      console.error('Import error:', err);
      toast.error('Failed to import audio file');
    }
  };

  // Open import modal (called from toolbar button)
  const openImportModal = () => {
    setShowImportModal(true);
  };

  // Delete track
  const deleteTrack = (trackId) => {
    setTracks(prev => prev.filter(t => t.id !== trackId));
    if (selectedTrackId === trackId) setSelectedTrackId(null);
  };

  // Toggle mute
  const toggleMute = (trackId) => {
    setTracks(prev => prev.map(t => {
      if (t.id === trackId) {
        const newMuted = !t.muted;
        // Apply mute to Tone.js player
        if (trackPlayersRef.current?.[trackId]) {
          trackPlayersRef.current[trackId].mute = newMuted;
        }
        return { ...t, muted: newMuted };
      }
      return t;
    }));
  };

  // Toggle solo
  const toggleSolo = (trackId) => {
    setTracks(prev => {
      const updated = prev.map(t => t.id === trackId ? { ...t, solo: !t.solo } : t);
      const anySoloed = updated.some(t => t.solo);
      // If any track is soloed, mute all non-soloed tracks
      updated.forEach(t => {
        if (trackPlayersRef.current?.[t.id]) {
          if (anySoloed) {
            trackPlayersRef.current[t.id].mute = !t.solo;
          } else {
            trackPlayersRef.current[t.id].mute = t.muted;
          }
        }
      });
      return updated;
    });
  };

  // Toggle arm
  const toggleArm = (trackId) => {
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { ...t, armed: !t.armed } : t
    ));
  };

  // Change volume
  const changeVolume = (trackId, volume) => {
    setTracks(prev => prev.map(t => {
      if (t.id === trackId) {
        // Apply volume to Tone.js player
        if (trackPlayersRef.current?.[trackId] && trackPlayersRef.current[trackId].volume) {
          // Convert 0-100 to dB (-60 to 0)
          const db = volume === 0 ? -Infinity : ((volume / 100) - 1) * 40;
          trackPlayersRef.current[trackId].volume.value = db;
        }
        return { ...t, volume };
      }
      return t;
    }));
  };

  // Change track name
  const changeTrackName = (trackId, name) => {
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { ...t, name } : t
    ));
  };

  // Move playhead (click on timeline)
  const movePlayhead = (beat) => {
    setCurrentBeat(beat);
    playheadOffsetRef.current = beat;
    if (isPlaying) {
      startTimeRef.current = Tone.now() - (beat * 60 / tempo);
    }
  };

  // Start recording
  const startRecording = async () => {
    const armedTracks = tracks.filter(t => t.armed && t.type === 'audio');
    if (armedTracks.length === 0) {
      toast.error('Arm a track first (click R)');
      return;
    }
    
    try {
      await Tone.start();
      
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false }
      });
      
      // Create analyser for live waveform
      const audioContext = audioContextRef.current;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      recordingAnalyserRef.current = analyser;
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      
      mediaRecorder.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        
        try {
          const arrayBuffer = await blob.arrayBuffer();
          const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
          
          // Extract waveform
          const rawData = audioBuffer.getChannelData(0);
          const samples = 2000;
          const blockSize = Math.floor(rawData.length / samples);
          const waveformData = [];
          
          for (let i = 0; i < samples; i++) {
            let sum = 0;
            for (let j = 0; j < blockSize; j++) {
              sum += Math.abs(rawData[i * blockSize + j] || 0);
            }
            waveformData.push(sum / blockSize);
          }
          
          const max = Math.max(...waveformData);
          const normalized = waveformData.map(v => v / max);
          
          // Update armed tracks
          setTracks(prev => prev.map(t => {
            if (t.armed && t.type === 'audio') {
              return { ...t, waveformData: normalized, audioBuffer, armed: false };
            }
            return t;
          }));
          
          toast.success('Recording saved!');
        } catch (decodeErr) {
          console.error('Audio decode error:', decodeErr);
          toast.error('Failed to process recording - try a different browser');
        }
        
        stream.getTracks().forEach(t => t.stop());
        recordingAnalyserRef.current = null;
      };
      
      mediaRecorder.start(100);
      setIsRecording(true);
      setIsPlaying(true);
      playStartBeatRef.current = currentBeat; // Store where recording started
      startTimeRef.current = Tone.now() - (currentBeat * 60 / tempo);
      Tone.Transport.start();
      
      // Animate playhead
      const animate = () => {
        const elapsed = Tone.now() - startTimeRef.current;
        const beat = (elapsed * tempo / 60);
        setCurrentBeat(beat);
        animationRef.current = requestAnimationFrame(animate);
      };
      animate();
      
      toast.success('Recording...');
    } catch (err) {
      console.error('Recording error:', err);
      toast.error('Recording failed');
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    setIsPlaying(false);
    cancelAnimationFrame(animationRef.current);
    Tone.Transport.stop();
    stopAllPlayers();
  };

  // Play/Pause with actual audio playback and effects
  const togglePlay = async () => {
    await Tone.start();
    
    if (isPlaying) {
      // Pause
      Tone.Transport.pause();
      setIsPlaying(false);
      cancelAnimationFrame(animationRef.current);
      
      // Pause all players
      Object.values(trackPlayersRef.current).forEach(player => {
        try { player.stop(); } catch (e) { console.warn('[StudioSession] player stop failed (safe to ignore):', e); }
      });
    } else {
      // Play
      playStartBeatRef.current = currentBeat;
      
      // Create players for all tracks with audio
      stopAllPlayers(); // Clean up first
      
      const playableTracks = tracks.filter(t => t.audioBuffer && !t.muted);
      const soloTracks = playableTracks.filter(t => t.solo);
      const tracksToPlay = soloTracks.length > 0 ? soloTracks : playableTracks;
      
      // Calculate start time in seconds based on current beat
      const startOffset = (currentBeat * 60) / tempo;
      
      for (const track of tracksToPlay) {
        try {
          // Convert AudioBuffer to Tone.js ToneAudioBuffer
          const toneBuffer = new Tone.ToneAudioBuffer(track.audioBuffer);
          const player = new Tone.Player(toneBuffer);
          
          // Build effect chain
          buildTrackEffectChain(track, player);
          
          // Store player reference
          trackPlayersRef.current[track.id] = player;
          
          // Start playing from the current position
          const trackDuration = track.audioBuffer.duration;
          if (startOffset < trackDuration) {
            player.start(Tone.now(), startOffset);
          }
        } catch (err) {
          console.error(`Error playing track ${track.name}:`, err);
        }
      }
      
      Tone.Transport.start();
      setIsPlaying(true);
      startTimeRef.current = Tone.now() - startOffset;
      
      const animate = () => {
        const elapsed = Tone.now() - startTimeRef.current;
        const newBeat = (elapsed * tempo) / 60;
        setCurrentBeat(newBeat);
        
        // Check if we've reached the end of all tracks
        const maxDuration = Math.max(...tracks.filter(t => t.audioBuffer).map(t => t.audioBuffer.duration), 0);
        if (elapsed >= maxDuration && maxDuration > 0) {
          // Auto-stop at end unless looping
          if (!loopEnabled) {
            stop();
            return;
          }
        }
        
        animationRef.current = requestAnimationFrame(animate);
      };
      animate();
      
      if (tracksToPlay.length > 0) {
        toast.success(`Playing ${tracksToPlay.length} track(s) with effects`);
      }
    }
  };

  // Stop - respects cursor stop mode
  const stop = () => {
    Tone.Transport.stop();
    setIsPlaying(false);
    setIsRecording(false);
    cancelAnimationFrame(animationRef.current);
    
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
    
    // Stop all audio playback
    stopAllPlayers();
    
    // Handle cursor position based on stop mode
    if (cursorStopMode === 'stay') {
      // Stay where we are - do nothing to currentBeat
    } else if (cursorStopMode === 'return-start') {
      // Return to where playback started
      setCurrentBeat(playStartBeatRef.current);
    } else if (cursorStopMode === 'return-zero') {
      // Return to beginning
      setCurrentBeat(0);
    }
  };

  // Rewind (go back 4 beats)
  const rewind = () => {
    const newBeat = Math.max(0, currentBeat - 4);
    movePlayhead(newBeat);
  };

  // Fast forward (go forward 4 beats)
  const fastForward = () => {
    const maxBeat = measures * beatsPerMeasure;
    const newBeat = Math.min(maxBeat, currentBeat + 4);
    movePlayhead(newBeat);
  };

  // Go to start
  const goToStart = () => {
    movePlayhead(0);
  };

  // Go to end
  const goToEnd = () => {
    movePlayhead(measures * beatsPerMeasure);
  };

  // End session - just hide the LiveKit widget
  const endSession = () => {
    setShowLiveKitVideo(false);
    setSessionActive(false);
    toast.info('Video session ended');
  };

  // Screen sharing state
  const [screenSharing, setScreenSharing] = useState(false);
  const screenStreamRef = useRef(null);
  
  // Note: Screen sharing via LiveKit is handled by the LiveKit SDK
  // This is a simplified placeholder for now
  const toggleScreenShare = () => {
    toast.info('Screen sharing is handled via LiveKit controls');
  };

  // Format time
  const formatTime = (beats) => {
    const totalSeconds = beats * 60 / tempo;
    const mins = Math.floor(totalSeconds / 60);
    const secs = Math.floor(totalSeconds % 60);
    const frames = Math.floor((totalSeconds % 1) * 30);
    return `${mins}:${secs.toString().padStart(2, '0')}:${frames.toString().padStart(2, '0')}`;
  };

  const formatBars = (beats) => {
    const bar = Math.floor(beats / beatsPerMeasure) + 1;
    const beat = Math.floor(beats % beatsPerMeasure) + 1;
    return `${bar}.${beat}`;
  };

  // Handle timeline ruler click
  const handleRulerClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const beat = x / (pixelsPerBeat * zoom);
    movePlayhead(Math.max(0, beat));
  };

  return (
    <div 
      className="daw-page h-screen bg-[#0a0a0a] text-white flex flex-col overflow-hidden relative"
      onDragEnter={handleGlobalDragEnter}
      onDragLeave={handleGlobalDragLeave}
      onDragOver={handleGlobalDragOver}
      onDrop={handleGlobalDrop}
    >
      {/* Global type-size and spacing boost for the whole DAW page. Text is
          pushed to a size older readers can see without straining, and every
          interactive control (button, input, select, slider, dropdown, track
          header, mixer strip) gets bigger tap targets so nothing feels
          cramped. Layout is intentionally airier — some pro-DAW density is
          traded for readability. */}
      <style>{`
        .daw-page { font-size: 18px; letter-spacing: 0.01em; }

        /* Type sizes */
        .daw-page .text-xs { font-size: 18px !important; line-height: 1.5 !important; }
        .daw-page .text-sm { font-size: 20px !important; line-height: 1.5 !important; }
        .daw-page .text-base { font-size: 22px !important; line-height: 1.5 !important; }
        .daw-page .text-lg { font-size: 26px !important; line-height: 1.4 !important; }
        .daw-page .text-xl { font-size: 30px !important; }
        .daw-page .text-2xl { font-size: 34px !important; }
        .daw-page .text-\\[7px\\],
        .daw-page .text-\\[8px\\],
        .daw-page .text-\\[9px\\],
        .daw-page .text-\\[10px\\],
        .daw-page .text-\\[11px\\] { font-size: 17px !important; line-height: 1.4 !important; }

        /* Bigger click targets */
        .daw-page button { min-height: 40px; padding-left: 12px; padding-right: 12px; }
        .daw-page button.p-1,
        .daw-page button.p-1\\.5,
        .daw-page button.p-2 { padding: 10px !important; min-height: 44px; min-width: 44px; }
        .daw-page button.p-2\\.5,
        .daw-page button.p-3 { padding: 12px !important; min-height: 48px; min-width: 48px; }

        /* Inputs and selects — much easier to hit and read */
        .daw-page input, .daw-page select, .daw-page textarea, .daw-page label { font-size: inherit; }
        .daw-page select,
        .daw-page input[type="text"],
        .daw-page input[type="email"],
        .daw-page input[type="number"],
        .daw-page input[type="search"],
        .daw-page input[type="date"],
        .daw-page input[type="time"] {
          font-size: 19px !important;
          min-height: 40px !important;
          padding: 8px 12px !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
        .daw-page input[type="range"] { height: 28px !important; }
        .daw-page input[type="checkbox"] { width: 22px !important; height: 22px !important; }

        /* Airier gaps in flex rows so controls stop touching each other */
        .daw-page .gap-0\\.5 { gap: 8px !important; }
        .daw-page .gap-1 { gap: 10px !important; }
        .daw-page .gap-1\\.5 { gap: 12px !important; }
        .daw-page .gap-2 { gap: 14px !important; }

        /* Icons — a bit bigger by default so tiny lucide-react icons don't
           get lost next to the enlarged text */
        .daw-page svg { flex-shrink: 0; }
      `}</style>
      <AdminFreeAccessBar
        title="Admin — Recording Studio (DAW) Free Access"
        subtitle="You're in the DAW. Start a new live session with a shareable code any time."
        testid="admin-bar-daw"
        primary={{
          label: 'Start Live Room',
          icon: Video,
          onClick: () => {
            const code = generateStudioRoomCode();
            window.location.href = `/video-room/${code}/host`;
          },
        }}
        secondary={{
          label: 'Back to Studio Page',
          onClick: () => { window.location.href = '/recording-studio'; },
        }}
      />
      <RecentRoomsPanel floating compact testid="daw-recent-rooms" />
      {/* Global Drop Overlay */}
      {globalDragOver && (
        <div className="absolute inset-0 z-[100] bg-amber-500/20 backdrop-blur-sm flex items-center justify-center pointer-events-none">
          <div className="bg-[#1a1a1a] border-4 border-dashed border-amber-400 rounded-2xl p-12 text-center">
            <Upload className="w-16 h-16 text-amber-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-amber-400 mb-2">Drop Audio Files Here</h2>
            <p className="text-gray-400">WAV, MP3, OGG, M4A, FLAC, AIFF supported</p>
          </div>
        </div>
      )}
      
      {/* LiveKit Video Widget - Production-grade SFU */}
      {sessionActive && showLiveKitVideo && (
        <LiveKitVideoWidget 
          roomCode={liveKitRoomCode}
          username="Host"
          isHost={true}
          minimizable={true}
          onClose={() => setShowLiveKitVideo(false)}
        />
      )}
      
      {/* Client Link Banner - Fixed at top of video area */}
      {sessionActive && (
        <div className="fixed top-12 right-4 z-40 bg-green-900/90 backdrop-blur px-4 py-2 rounded-lg border border-green-600 max-w-sm">
          <p className="text-xs text-green-300 font-medium mb-1">Share this link with your client:</p>
          <div className="flex items-center gap-2">
            <code className="text-xs text-green-400 bg-black/50 px-2 py-1 rounded flex-1 truncate">
              {window.location.origin}/livekit-room/{liveKitRoomCode}
            </code>
            <button
              onClick={copyClientLink}
              className="px-2 py-1 bg-green-600 hover:bg-green-500 rounded text-xs font-bold"
            >
              Copy
            </button>
          </div>
        </div>
      )}
      
      {/* Show/Hide Video Button when hidden */}
      {sessionActive && !showLiveKitVideo && (
        <button
          onClick={() => setShowLiveKitVideo(true)}
          className="fixed bottom-4 right-4 z-50 p-4 bg-green-600 hover:bg-green-500 rounded-full shadow-xl flex items-center gap-2"
        >
          <Video className="w-6 h-6 text-white" />
          <span className="text-white font-bold">Show Video</span>
        </button>
      )}

      {/* ============ TOP TOOLBAR (minimal - files & device) ============ */}
      <div className="min-h-[60px] bg-[#1a1a1a] border-b border-[#333] flex items-center px-4 gap-3 text-base [&_svg]:w-5 [&_svg]:h-5">
        {/* Left - Navigation & File */}
        <Link to="/admin" className="p-2.5 hover:bg-[#333] rounded" title="Back to Admin">
          <ArrowLeft />
        </Link>
        <div className="w-px h-10 bg-[#333]" />
        
        <button className="px-4 py-2.5 hover:bg-[#333] rounded text-base font-semibold flex items-center gap-2">
          <Save /> Save
        </button>
        <button className="px-4 py-2.5 hover:bg-[#333] rounded text-base font-semibold flex items-center gap-2">
          <FolderOpen /> Open
        </button>
        <button 
          onClick={() => setShowAudioSettings(true)}
          className="px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-black rounded-lg text-base font-bold flex items-center gap-2 shadow-lg ring-2 ring-amber-300/60"
          title="Choose your audio input & output device (mic, interface, speakers)"
          data-testid="daw-audio-settings-btn"
        >
          <Settings /> Audio Device
        </button>

        <div className="flex-1" />

        {/* Right - Session status only */}
        {!sessionActive ? (
          <button
            onClick={() => toast.info('Session start not yet available')}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded text-sm font-medium flex items-center gap-2"
          >
            <Video className="w-4 h-4" /> Start Session
          </button>
        ) : (
          <div className="flex items-center gap-2">
            {clientConnected ? (
              <span className="text-sm text-green-400 flex items-center gap-2">
                <Users className="w-4 h-4" /> Connected
              </span>
            ) : (
              <span className="text-sm text-amber-400 animate-pulse flex items-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin" /> Waiting...
              </span>
            )}
            <button
              onClick={endSession}
              className="px-3 py-2 bg-red-600 hover:bg-red-700 rounded text-sm"
            >
              <PhoneOff className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* ============ LEVELS + TRANSPORT + DEVICE STRIP ============ */}
      <div className="bg-[#0f0f0f] border-b border-[#333] px-4 py-3 flex items-stretch gap-5">
        
        {/* Section 1 — METERS */}
        <div className="flex items-start gap-5 pr-5 border-r border-[#2a2a2a]">
          <div>
            <div className="text-[13px] text-amber-400 font-black tracking-widest mb-2">METERS</div>
            <div className="flex gap-5">
              <BigLevelMeter label="IN" subtitle="Mic / Interface" level={inputMeterLevel} peak={inputMeterLevel} />
              <BigLevelMeter label="OUT" subtitle="Master" level={masterMeterLevel} peak={masterMeterLevel} />
            </div>
          </div>
        </div>

        {/* Section 2 — TOOLS on the left · TRANSPORT as one unit on the right */}
        <div className="flex-1 flex items-center gap-4 pr-5 border-r border-[#2a2a2a] min-w-0">

          {/* LEFT SIDE — supporting tools, room here for more later */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center bg-[#0a0a0a] rounded">
              {[
                { id: 'select', icon: MousePointer2, title: 'Select' },
                { id: 'hand', icon: Hand, title: 'Hand' },
                { id: 'scissors', icon: Scissors, title: 'Split' },
              ].map(tool => (
                <button
                  key={tool.id}
                  onClick={() => setSelectedTool(tool.id)}
                  className={`p-3 ${selectedTool === tool.id ? 'bg-[#333] text-amber-400' : 'hover:bg-[#222]'}`}
                  title={tool.title}
                >
                  <tool.icon className="w-6 h-6" />
                </button>
              ))}
            </div>

            <div className="flex items-center gap-1 bg-[#0a0a0a] rounded p-1">
              <button onClick={cutSelection} className="p-3 hover:bg-[#333] rounded disabled:opacity-30" title="Cut (Ctrl+X)" disabled={selectionStart === null || !selectedTrackId}>
                <Scissors className="w-6 h-6" />
              </button>
              <button onClick={copySelection} className="p-3 hover:bg-[#333] rounded disabled:opacity-30" title="Copy (Ctrl+C)" disabled={selectionStart === null || !selectedTrackId}>
                <Copy className="w-6 h-6" />
              </button>
              <button onClick={pasteClipboard} className="p-3 hover:bg-[#333] rounded disabled:opacity-30 min-w-12" title="Paste (Ctrl+V)" disabled={!clipboard || !selectedTrackId}>
                <span className="text-lg font-bold">P</span>
              </button>
              <button onClick={trimToSelection} className="p-3 hover:bg-[#333] rounded disabled:opacity-30 min-w-12" title="Trim to Selection" disabled={selectionStart === null || !selectedTrackId}>
                <span className="text-lg font-bold">T</span>
              </button>
              <button onClick={deleteSelection} className="p-3 hover:bg-[#333] rounded text-red-400 disabled:opacity-30" title="Delete Selection (Del)" disabled={selectionStart === null || !selectedTrackId}>
                <Trash2 className="w-6 h-6" />
              </button>
            </div>

            <div className="flex items-center gap-1 bg-[#0a0a0a] rounded p-1">
              <button className="p-3 hover:bg-[#333] rounded" title="Undo"><Undo2 className="w-6 h-6" /></button>
              <button className="p-3 hover:bg-[#333] rounded" title="Redo"><Redo2 className="w-6 h-6" /></button>
            </div>

            <div className="flex items-center gap-2 bg-black px-3 py-2 rounded font-mono text-base text-white">
              <span className="text-gray-400">{formatBars(currentBeat)}</span>
              <span className="text-[#333]">|</span>
              <span>{formatTime(currentBeat)}</span>
            </div>
            <div className="flex items-center gap-2 bg-black px-3 py-2 rounded">
              <span className="text-sm font-bold text-amber-400">BPM</span>
              <input
                type="number"
                value={tempo}
                onChange={(e) => setTempo(Math.max(20, Math.min(300, parseInt(e.target.value) || 120)))}
                className="w-16 bg-transparent text-lg font-bold text-center outline-none text-white"
              />
            </div>
          </div>

          <div className="flex-1" />

          {/* RIGHT SIDE — TRANSPORT AS ONE UNIT (all buttons together in one bar) */}
          <div className="flex items-center gap-1.5 bg-[#0a0a0a] rounded-xl p-2 ring-1 ring-[#2a2a2a] shadow-inner">
            <button onClick={goToStart} className="p-3 bg-[#333] hover:bg-[#444] rounded" title="Go to Start">
              <ChevronsLeft className="w-6 h-6" />
            </button>
            <button onClick={rewind} className="p-3 bg-[#333] hover:bg-[#444] rounded" title="Rewind">
              <Rewind className="w-6 h-6" />
            </button>
            <button onClick={stop} className="p-3 bg-[#333] hover:bg-[#444] rounded" title="Stop (S)" data-testid="transport-stop">
              <Square className="w-6 h-6" />
            </button>
            <button 
              data-testid="transport-play"
              onClick={togglePlay}
              className={`px-8 py-3 rounded shadow-lg ${isPlaying ? 'bg-amber-500 hover:bg-amber-400 text-black' : 'bg-green-600 hover:bg-green-500 text-white'}`}
              title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
            >
              {isPlaying ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7" />}
            </button>
            <button 
              data-testid="transport-record"
              onClick={isRecording ? stopRecording : startRecording}
              className={`p-3 rounded ${isRecording ? 'bg-red-500 animate-pulse' : 'bg-[#333] hover:bg-red-600'}`}
              title={isRecording ? 'Stop Recording (R)' : 'Record (R)'}
            >
              <Circle className={`w-6 h-6 ${isRecording ? 'fill-current' : ''}`} />
            </button>
            <button onClick={fastForward} className="p-3 bg-[#333] hover:bg-[#444] rounded" title="Fast Forward">
              <FastForward className="w-6 h-6" />
            </button>
            <button onClick={goToEnd} className="p-3 bg-[#333] hover:bg-[#444] rounded" title="Go to End">
              <ChevronsRight className="w-6 h-6" />
            </button>
            <button 
              onClick={() => setLoopEnabled(!loopEnabled)}
              className={`p-3 rounded ${loopEnabled ? 'bg-blue-500 hover:bg-blue-400' : 'bg-[#333] hover:bg-[#444]'}`}
              title="Loop"
            >
              <Repeat className="w-6 h-6" />
            </button>
            <button 
              onClick={() => setFollowPlayhead(!followPlayhead)}
              className={`px-3 py-3 rounded flex items-center gap-2 ${followPlayhead ? 'bg-green-600' : 'bg-[#333] hover:bg-[#444]'}`}
              title={followPlayhead ? 'Following playhead — click to disable' : 'Click to follow playhead'}
            >
              <Locate className="w-6 h-6" />
              <span className="text-sm font-bold">{followPlayhead ? 'FOLLOW' : 'SCROLL'}</span>
            </button>
            <div className="relative" data-cursor-menu>
              <button 
                onClick={() => setShowCursorMenu(!showCursorMenu)}
                className={`p-3 rounded flex items-center gap-1 ${showCursorMenu ? 'bg-[#444]' : 'bg-[#333] hover:bg-[#444]'}`}
                title="Cursor Stop Mode"
              >
                <MousePointer2 className="w-6 h-6" />
                <ChevronDown className="w-4 h-4" />
              </button>
              {showCursorMenu && (
                <div className="absolute top-full mt-1 right-0 bg-[#222] border border-[#444] rounded-lg shadow-xl z-50 min-w-56 py-1">
                  <div className="px-3 py-1 text-xs text-gray-500 uppercase tracking-wide border-b border-[#333]">On Stop, Cursor Will:</div>
                  <button onClick={() => { setCursorStopMode('stay'); setShowCursorMenu(false); }} className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-[#333] ${cursorStopMode === 'stay' ? 'text-amber-400' : ''}`}>
                    <div className={`w-3 h-3 rounded-full border-2 ${cursorStopMode === 'stay' ? 'border-amber-400 bg-amber-400' : 'border-gray-500'}`} />
                    Stay in Place
                    <span className="text-xs text-gray-500 ml-auto">Best for editing</span>
                  </button>
                  <button onClick={() => { setCursorStopMode('return-start'); setShowCursorMenu(false); }} className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-[#333] ${cursorStopMode === 'return-start' ? 'text-amber-400' : ''}`}>
                    <div className={`w-3 h-3 rounded-full border-2 ${cursorStopMode === 'return-start' ? 'border-amber-400 bg-amber-400' : 'border-gray-500'}`} />
                    Return to Start Point
                    <span className="text-xs text-gray-500 ml-auto">Loop practice</span>
                  </button>
                  <button onClick={() => { setCursorStopMode('return-zero'); setShowCursorMenu(false); }} className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-[#333] ${cursorStopMode === 'return-zero' ? 'text-amber-400' : ''}`}>
                    <div className={`w-3 h-3 rounded-full border-2 ${cursorStopMode === 'return-zero' ? 'border-amber-400 bg-amber-400' : 'border-gray-500'}`} />
                    Return to Beginning
                    <span className="text-xs text-gray-500 ml-auto">Full playback</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section 3 — TRACKS (Add + Floating Transport toggle) */}
        <div className="flex flex-col items-stretch gap-2 min-w-[220px]">
          <div className="text-[13px] text-amber-400 font-black tracking-widest">TRACKS</div>
          <button
            onClick={() => addTrack('audio')}
            className="px-4 py-3 bg-green-700 hover:bg-green-600 rounded-lg text-base font-semibold flex items-center gap-2 justify-center"
            data-testid="add-audio-track-big"
          >
            <Plus className="w-5 h-5" /> Add Audio Track
          </button>
          <button
            onClick={() => addTrack('midi')}
            className="px-4 py-3 bg-blue-700 hover:bg-blue-600 rounded-lg text-base font-semibold flex items-center gap-2 justify-center"
            data-testid="add-midi-track-big"
          >
            <Plus className="w-5 h-5" /> Add MIDI Track
          </button>
          <button
            onClick={() => setFloatingTransportVisible(v => !v)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 justify-center ${
              floatingTransportVisible ? 'bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white' : 'bg-amber-500 hover:bg-amber-400 text-black'
            }`}
            data-testid="toggle-floating-transport"
          >
            {floatingTransportVisible ? 'Hide' : 'Show'} Floating Transport
          </button>
        </div>
      </div>

      {/* ============ SECONDARY TOOLBAR ============ */}
      <div className="h-8 bg-[#151515] border-b border-[#333] flex items-center px-2 gap-2">
        <button
          onClick={() => addTrack('audio')}
          className="px-2 py-1 bg-green-700 hover:bg-green-600 rounded text-xs flex items-center gap-1"
        >
          <Plus className="w-3 h-3" /> Audio Track
        </button>
        <button
          onClick={() => addTrack('midi')}
          className="px-2 py-1 bg-blue-700 hover:bg-blue-600 rounded text-xs flex items-center gap-1"
        >
          <Plus className="w-3 h-3" /> MIDI Track
        </button>
        <button
          onClick={openImportModal}
          className="px-2 py-1 bg-amber-700 hover:bg-amber-600 rounded text-xs flex items-center gap-1"
          data-testid="import-audio-btn"
        >
          <Upload className="w-3 h-3" /> Import Audio
        </button>
        <button
          onClick={exportSession}
          disabled={isExporting || tracks.filter(t => t.audioBuffer).length === 0}
          className="px-2 py-1 bg-green-700 hover:bg-green-600 disabled:bg-gray-700 disabled:cursor-not-allowed rounded text-xs flex items-center gap-1"
          title="Export session to WAV file"
        >
          <Download className="w-3 h-3" /> {isExporting ? 'Exporting...' : 'Export'}
        </button>
        
        <div className="w-px h-5 bg-[#333]" />
        
        {/* Metronome */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => setMetronomeEnabled(!metronomeEnabled)}
            className={`p-1.5 rounded flex items-center gap-1 ${metronomeEnabled ? 'bg-amber-500 text-black' : 'bg-[#333] hover:bg-[#444]'}`}
            title="Metronome"
          >
            <Timer className="w-4 h-4" />
          </button>
          {metronomeEnabled && (
            <input
              type="range"
              min="0"
              max="100"
              value={metronomeVolume}
              onChange={(e) => setMetronomeVolume(parseInt(e.target.value))}
              className="w-12 h-1 accent-amber-500"
              title={`Metronome: ${metronomeVolume}%`}
            />
          )}
        </div>
        
        <div className="w-px h-5 bg-[#333]" />
        
        {/* Quantize Menu */}
        <div className="relative" data-quantize-menu>
          <button
            onClick={() => setShowQuantizeMenu(!showQuantizeMenu)}
            className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${quantizeValue !== 'Off' ? 'bg-purple-600' : 'bg-[#333] hover:bg-[#444]'}`}
            title="Quantize"
          >
            <Magnet className="w-3 h-3" /> Q: {quantizeValue}
            <ChevronDown className="w-3 h-3" />
          </button>
          
          {showQuantizeMenu && (
            <div className="absolute top-full mt-1 left-0 bg-[#222] border border-[#444] rounded-lg shadow-xl z-50 py-1 min-w-24">
              <div className="px-3 py-1 text-[10px] text-gray-500 uppercase tracking-wide border-b border-[#333]">
                Quantize
              </div>
              {quantizeOptions.map(opt => (
                <button
                  key={opt}
                  onClick={() => { setQuantizeValue(opt); setShowQuantizeMenu(false); }}
                  className={`w-full px-3 py-1.5 text-left text-sm hover:bg-[#333] ${quantizeValue === opt ? 'text-purple-400' : ''}`}
                >
                  {opt}
                </button>
              ))}
            </div>
          )}
        </div>
        
        <div className="w-px h-5 bg-[#333]" />
        
        <button 
          onClick={() => setZoom(Math.max(0.25, zoom - 0.25))}
          className="p-1 hover:bg-[#333] rounded"
        >
          <ZoomOut className="w-4 h-4" />
        </button>
        <span className="text-xs text-gray-400 w-12 text-center">{Math.round(zoom * 100)}%</span>
        <button 
          onClick={() => setZoom(Math.min(4, zoom + 0.25))}
          className="p-1 hover:bg-[#333] rounded"
        >
          <ZoomIn className="w-4 h-4" />
        </button>
        
        <div className="flex-1" />
        
        {/* FX Panel Toggle */}
        <button
          onClick={() => {
            if (!showFxPanel && selectedTrackId) {
              setSelectedTrackForFx(selectedTrackId);
            }
            setShowFxPanel(!showFxPanel);
          }}
          className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${showFxPanel ? 'bg-purple-600' : 'hover:bg-[#333]'}`}
        >
          <Waves className="w-3 h-3" /> FX
        </button>
        
        <button
          onClick={() => setShowMixer(!showMixer)}
          className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${showMixer ? 'bg-[#333]' : 'hover:bg-[#333]'}`}
        >
          <Sliders className="w-3 h-3" /> Mixer
        </button>
      </div>

      {/* ============ MAIN CONTENT ============ */}
      <div className="flex-1 flex overflow-hidden">
        {/* Track List + Timeline */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Timeline Ruler - CLICKABLE */}
          <div className="h-6 bg-[#1a1a1a] border-b border-[#333] flex">
            <div className="w-80 min-w-80 border-r border-[#333]" />
            <div 
              className="flex-1 relative overflow-hidden cursor-pointer"
              onClick={handleRulerClick}
            >
              {/* Bar numbers */}
              {Array.from({ length: measures + 1 }).map((_, i) => (
                <div
                  key={i}
                  className="absolute top-0 bottom-0 text-[10px] text-gray-500 border-l border-[#333]"
                  style={{ left: `${i * beatsPerMeasure * pixelsPerBeat * zoom}px` }}
                >
                  <span className="ml-1">{i + 1}</span>
                </div>
              ))}
              
              {/* Playhead on ruler */}
              <div 
                className="absolute top-0 w-0 h-0 border-l-4 border-r-4 border-t-8 border-l-transparent border-r-transparent border-t-amber-400"
                style={{ left: `${currentBeat * pixelsPerBeat * zoom - 4}px` }}
              />
            </div>
          </div>
          
          {/* Tracks */}
          <div className="flex-1 overflow-auto" id="tracks-scroll-container">
            {tracks.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-gray-500">
                <Music className="w-16 h-16 mb-4 opacity-20" />
                <p className="text-lg mb-2">No tracks yet</p>
                <p className="text-sm">Click "+ Audio Track" or "+ MIDI Track" to begin</p>
              </div>
            ) : (
              <div className="relative">
                {tracks.map((track, index) => (
                  <Track
                    key={track.id}
                    track={track}
                    index={index}
                    isSelected={selectedTrackId === track.id}
                    onSelect={setSelectedTrackId}
                    onDelete={deleteTrack}
                    onMute={toggleMute}
                    onSolo={toggleSolo}
                    onArm={toggleArm}
                    onVolumeChange={changeVolume}
                    onNameChange={changeTrackName}
                    onInputChange={changeInputDevice}
                    onOpenFx={openFxPanel}
                    onMoveAudio={moveAudioClip}
                    onReorderTrack={reorderTrack}
                    onMoveAudioToTrack={moveAudioToTrack}
                    tempo={tempo}
                    isRecording={isRecording}
                    analyser={recordingAnalyserRef.current}
                    zoom={zoom}
                    measures={measures}
                    beatsPerMeasure={beatsPerMeasure}
                    pixelsPerBeat={pixelsPerBeat}
                    currentBeat={currentBeat}
                    onTimelineClick={movePlayhead}
                    audioInputDevices={audioInputDevices}
                    inputChannelConfig={inputChannelConfig}
                    selectionStart={selectedTrackId === track.id ? selectionStart : null}
                    selectionEnd={selectedTrackId === track.id ? selectionEnd : null}
                    onSetSelection={setWaveformSelection}
                    totalTracks={tracks.length}
                  />
                ))}
                
                {/* Global Playhead */}
                <div 
                  className="absolute top-0 bottom-0 w-px bg-amber-400 pointer-events-none z-10"
                  style={{ left: `${208 + currentBeat * pixelsPerBeat * zoom}px` }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Mixer Panel */}
        <AnimatePresence>
          {showMixer && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 'auto', opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              className="bg-[#151515] border-l border-[#333] flex flex-col"
            >
              <div className="p-2 border-b border-[#333] text-xs font-medium flex items-center gap-2">
                <Sliders className="w-3 h-3" /> MIXER
              </div>
              <div className="flex-1 overflow-x-auto p-2 flex gap-2">
                {tracks.map(track => (
                  <MixerChannel
                    key={track.id}
                    track={track}
                    onVolumeChange={changeVolume}
                    onMute={toggleMute}
                    onSolo={toggleSolo}
                    onArm={toggleArm}
                    onPanChange={changePan}
                    onEQChange={changeEQ}
                    onFxClick={(trackId) => {
                      setSelectedTrackForFx(trackId);
                      setShowFxPanel(true);
                    }}
                    meterLevel={meterLevels[track.id]?.level || 0}
                    meterPeak={meterLevels[track.id]?.peak || 0}
                    inputLevel={track.armed ? (meterLevels[track.id]?.level || 0) * 0.8 : 0}
                    isRecording={isRecording}
                  />
                ))}
                {/* Master Channel with VU and Compressor */}
                <div className="w-28 bg-[#1a1a1a] rounded-lg p-2 flex flex-col items-center gap-1.5 border border-amber-500/30">
                  {/* Master Compressor */}
                  <div className="flex gap-1 mb-1">
                    <div className="flex flex-col items-center">
                      <div className="w-6 h-6 rounded-full bg-[#333] border border-amber-500/50 flex items-center justify-center cursor-ns-resize">
                        <div className="w-0.5 h-2 bg-amber-400 rounded" style={{ transform: 'rotate(-45deg)' }} />
                      </div>
                      <span className="text-[7px] text-gray-500">THR</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-6 h-6 rounded-full bg-[#333] border border-amber-500/50 flex items-center justify-center cursor-ns-resize">
                        <div className="w-0.5 h-2 bg-amber-400 rounded" />
                      </div>
                      <span className="text-[7px] text-gray-500">RAT</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-6 h-6 rounded-full bg-[#333] border border-amber-500/50 flex items-center justify-center cursor-ns-resize">
                        <div className="w-0.5 h-2 bg-amber-400 rounded" style={{ transform: 'rotate(30deg)' }} />
                      </div>
                      <span className="text-[7px] text-gray-500">MKP</span>
                    </div>
                  </div>
                  <span className="text-[7px] text-amber-400/70 tracking-wider">COMPRESSOR</span>
                  
                  {/* Master Pan */}
                  <div className="w-8 h-8 rounded-full bg-[#333] border-2 border-amber-500/50 flex items-center justify-center">
                    <div className="w-0.5 h-3 bg-amber-400 rounded" />
                  </div>
                  <span className="text-[8px] text-gray-500">PAN</span>
                  
                  {/* Master Fader with VU */}
                  <div className="flex gap-1">
                    <VUMeter
                      level={masterMeterLevel}
                      peak={masterMeterLevel}
                    />
                    <div className="h-28 w-4 bg-[#0a0a0a] rounded relative flex flex-col items-center py-1">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={masterVolume}
                        onChange={(e) => setMasterVolume(parseInt(e.target.value))}
                        className="h-24 w-1.5 accent-amber-500 cursor-pointer"
                        style={{ writingMode: 'vertical-lr', direction: 'rtl' }}
                      />
                    </div>
                  </div>
                  <span className="text-[9px] text-amber-400 font-mono">{masterVolume}</span>
                  
                  <span className="text-[9px] text-amber-400 font-bold">MASTER</span>
                </div>
                
                {/* Transport Controls in Mixer */}
                <div className="w-20 bg-[#1a1a1a] rounded-lg p-2 flex flex-col items-center justify-center gap-2 border border-green-500/30">
                  <span className="text-[8px] text-gray-500 uppercase tracking-wider">TRANSPORT</span>
                  
                  {/* Play/Stop Row */}
                  <div className="flex gap-1">
                    <button
                      onClick={togglePlay}
                      className={`p-2 rounded ${isPlaying ? 'bg-amber-500 text-black' : 'bg-green-600 hover:bg-green-700'}`}
                      title="Play/Pause (Space)"
                    >
                      {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={stop}
                      className="p-2 bg-[#333] hover:bg-[#444] rounded"
                      title="Stop"
                    >
                      <Square className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Record Button - Prominent */}
                  <button
                    onClick={isRecording ? stopRecording : startRecording}
                    className={`w-full py-2 rounded-lg flex items-center justify-center gap-1 ${
                      isRecording 
                        ? 'bg-red-500 animate-pulse' 
                        : 'bg-red-600/80 hover:bg-red-600'
                    }`}
                    title="Record (R)"
                  >
                    <Circle className={`w-4 h-4 ${isRecording ? 'fill-current' : ''}`} />
                    <span className="text-[10px] font-bold">{isRecording ? 'STOP' : 'REC'}</span>
                  </button>
                  
                  {/* Keyboard Shortcuts Hint */}
                  <div className="text-[7px] text-gray-600 text-center leading-tight">
                    Space: Play<br/>
                    R: Record<br/>
                    M: Mute
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ============ FX PANEL (Floating) ============ */}
      <AnimatePresence>
        {showFxPanel && (
          <motion.div
            initial={{ opacity: 0, x: 300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 300 }}
            className="fixed right-4 top-24 bottom-24 w-72 bg-[#1a1a1a] border border-[#333] rounded-lg shadow-2xl z-40 flex flex-col"
          >
            {/* FX Panel Header */}
            <div className="p-3 border-b border-[#333] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Waves className="w-4 h-4 text-purple-400" />
                <span className="font-medium text-sm">
                  {selectedTrackForFx ? `FX: ${tracks.find(t => t.id === selectedTrackForFx)?.name}` : 'Effects'}
                </span>
              </div>
              <button
                onClick={() => setShowFxPanel(false)}
                className="p-1 hover:bg-[#333] rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            {!selectedTrackForFx ? (
              <div className="flex-1 flex flex-col items-center justify-center text-gray-500 p-4">
                <Waves className="w-12 h-12 mb-4 opacity-20" />
                <p className="text-sm text-center">Select a track to add effects</p>
                <p className="text-xs text-center mt-2">Click the FX button on any track</p>
              </div>
            ) : (
              <>
                {/* Active Effects - Draggable to reorder */}
                <div className="p-3 border-b border-[#333]">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-2">
                    Active Effects (Drag to Reorder)
                  </div>
                  {(() => {
                    const track = tracks.find(t => t.id === selectedTrackForFx);
                    if (!track?.effects?.length) {
                      return <div className="text-xs text-gray-600 py-2">No effects added</div>;
                    }
                    return (
                      <div className="flex flex-col gap-1">
                        {track.effects.map((effectId, index) => {
                          const effect = availableEffects.find(e => e.id === effectId);
                          const isSelected = selectedEffectForEdit === effectId;
                          const isDragging = draggingEffect === index;
                          const isDragOver = dragOverEffect === index;
                          
                          return (
                            <div
                              key={effectId}
                              draggable
                              onDragStart={() => setDraggingEffect(index)}
                              onDragEnd={() => { setDraggingEffect(null); setDragOverEffect(null); }}
                              onDragOver={(e) => { e.preventDefault(); setDragOverEffect(index); }}
                              onDrop={() => {
                                if (draggingEffect !== null && draggingEffect !== index) {
                                  reorderEffects(selectedTrackForFx, draggingEffect, index);
                                }
                                setDraggingEffect(null);
                                setDragOverEffect(null);
                              }}
                              onClick={() => setSelectedEffectForEdit(isSelected ? null : effectId)}
                              className={`px-3 py-2 rounded text-xs flex items-center justify-between cursor-grab transition-all ${
                                isDragging ? 'opacity-50 scale-95' : ''
                              } ${isDragOver ? 'border-2 border-purple-400' : 'border-2 border-transparent'} ${
                                isSelected ? 'bg-purple-600 text-white' : 'bg-purple-600/30 hover:bg-purple-600/50'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-gray-400 text-[10px] font-mono">{index + 1}</span>
                                <span>{effect?.name}</span>
                              </div>
                              <button
                                onClick={(e) => { e.stopPropagation(); removeEffectFromTrack(selectedTrackForFx, effectId); }}
                                className="opacity-0 group-hover:opacity-100 hover:text-red-400 p-1"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
                
                {/* Effect Parameter Editor */}
                {selectedEffectForEdit && (() => {
                  const effectDef = availableEffects.find(e => e.id === selectedEffectForEdit);
                  if (!effectDef) return null;
                  const currentParams = getEffectParams(selectedTrackForFx, selectedEffectForEdit);
                  
                  return (
                    <div className="p-3 border-b border-[#333] bg-[#0d0d0d]">
                      <div className="text-[10px] text-purple-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                        <Sliders className="w-3 h-3" />
                        {effectDef.name} Parameters
                      </div>
                      <div className="space-y-3">
                        {Object.entries(currentParams).map(([param, value]) => {
                          // Define ranges for each parameter
                          const paramConfig = {
                            decay:         { min: 0.1, max: 10,   step: 0.1,   unit: 's' },
                            wet:           { min: 0,   max: 1,    step: 0.01,  unit: '%', scale: 100 },
                            delayTime:     { min: 0.01,max: 1,    step: 0.01,  unit: 's' },
                            feedback:      { min: 0,   max: 0.95, step: 0.01,  unit: '%', scale: 100 },
                            low:           { min: -24, max: 24,   step: 1,     unit: 'dB' },
                            mid:           { min: -24, max: 24,   step: 1,     unit: 'dB' },
                            high:          { min: -24, max: 24,   step: 1,     unit: 'dB' },
                            threshold:     { min: -60, max: 0,    step: 1,     unit: 'dB' },
                            ratio:         { min: 1,   max: 20,   step: 0.5,   unit: ':1' },
                            attack:        { min: 0.001,max: 1,   step: 0.001, unit: 's' },
                            release:       { min: 0.01,max: 2,    step: 0.01,  unit: 's' },
                            distortion:    { min: 0,   max: 1,    step: 0.01,  unit: '%', scale: 100 },
                            frequency:     { min: 0.1, max: 10,   step: 0.1,   unit: 'Hz' },
                            depth:         { min: 0,   max: 1,    step: 0.01,  unit: '%', scale: 100 },
                            preDelay:      { min: 0,   max: 0.2,  step: 0.005, unit: 's' },
                            roomSize:      { min: 0,   max: 1,    step: 0.01,  unit: '%', scale: 100 },
                            dampening:     { min: 0,   max: 1,    step: 0.01,  unit: '%', scale: 100 },
                            Q:             { min: 0.1, max: 20,   step: 0.1,   unit: '' },
                            baseFrequency: { min: 20,  max: 2000, step: 10,    unit: 'Hz' },
                            octaves:       { min: 0.5, max: 8,    step: 0.5,   unit: 'oct' },
                            sensitivity:   { min: -40, max: 0,    step: 1,     unit: 'dB' },
                            bits:          { min: 1,   max: 16,   step: 1,     unit: 'bit' },
                            order:         { min: 1,   max: 100,  step: 1,     unit: '' },
                            width:         { min: 0,   max: 1,    step: 0.01,  unit: '%', scale: 100 },
                          };
                          // Frequency scale differs between LFO effects (0.1-10 Hz)
                          // and filter effects (20-20 000 Hz). Detect by initial value.
                          if (param === 'frequency' && value > 50) {
                            paramConfig.frequency = { min: 20, max: 20000, step: 10, unit: 'Hz' };
                          }
                          const config = paramConfig[param] || { min: 0, max: 1, step: 0.01, unit: '' };
                          const displayValue = config.scale ? (value * config.scale).toFixed(0) : value.toFixed(config.step >= 1 ? 0 : config.step >= 0.1 ? 1 : config.step >= 0.01 ? 2 : 3);
                          
                          return (
                            <div key={param} className="space-y-1">
                              <div className="flex justify-between text-[10px]">
                                <span className="text-gray-400 capitalize">{param.replace(/([A-Z])/g, ' $1')}</span>
                                <span className="text-purple-400 font-mono">{displayValue}{config.unit}</span>
                              </div>
                              <input
                                type="range"
                                min={config.min}
                                max={config.max}
                                step={config.step}
                                value={value}
                                onChange={(e) => updateEffectParam(selectedTrackForFx, selectedEffectForEdit, param, parseFloat(e.target.value))}
                                className="w-full h-2 accent-purple-500 cursor-pointer"
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
                
                {/* Available Effects — grouped by category */}
                <div className="flex-1 overflow-y-auto p-3">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-2">Add Effects · {availableEffects.length} plugins</div>
                  {Array.from(new Set(availableEffects.map(e => e.category))).map(category => {
                    const catEffects = availableEffects.filter(e => e.category === category);
                    const catColor = catEffects[0]?.color || 'gray';
                    return (
                      <div key={category} className="mb-3">
                        <div className={`text-[11px] font-black uppercase tracking-wider mb-1.5 text-${catColor}-400`}>
                          {category}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {catEffects.map(effect => {
                            const track = tracks.find(t => t.id === selectedTrackForFx);
                            const isActive = track?.effects?.includes(effect.id);
                            return (
                              <button
                                key={effect.id}
                                onClick={() => !isActive && addEffectToTrack(selectedTrackForFx, effect.id)}
                                disabled={isActive}
                                className={`p-3 rounded-lg text-left transition-all ${
                                  isActive
                                    ? `bg-${effect.color}-600/20 border border-${effect.color}-500/50 cursor-default`
                                    : `bg-[#222] hover:bg-[#333] border border-[#333] hover:border-${effect.color}-500/50`
                                }`}
                        >
                          <div className="font-medium text-sm flex items-center justify-between">
                            {effect.name}
                            {isActive && <Check className="w-3 h-3 text-purple-400" />}
                          </div>
                          <div className="text-[10px] text-gray-500 mt-1">
                            {Object.entries(effect.params).slice(0, 2).map(([k, v]) => (
                              <span key={k} className="mr-2">{k}: {v}</span>
                            ))}
                          </div>
                                             </button>
                      );
                    })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Tip */}
                <div className="p-3 border-t border-[#333] bg-[#0a0a0a]">
                  <div className="text-[10px] text-gray-500">
                    Tip: Click an active effect to edit its parameters. Changes apply in real-time during playback.
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ============ STATUS BAR ============ */}
      <div className="h-6 bg-[#1a1a1a] border-t border-[#333] px-3 flex items-center justify-between text-[10px] text-gray-500">
        <div className="flex items-center gap-4">
          <span>{tracks.length} tracks</span>
          <span>Selected: {selectedTrackId ? tracks.find(t => t.id === selectedTrackId)?.name : 'None'}</span>
          <span className="flex items-center gap-1">
            <MousePointer2 className="w-3 h-3" />
            Stop: {cursorStopMode === 'stay' ? 'Stay' : cursorStopMode === 'return-start' ? 'Start' : 'Zero'}
          </span>
          {metronomeEnabled && (
            <span className="flex items-center gap-1 text-amber-400">
              <Timer className="w-3 h-3" /> Metro ON
            </span>
          )}
          {quantizeValue !== 'Off' && (
            <span className="flex items-center gap-1 text-purple-400">
              <Magnet className="w-3 h-3" /> Q: {quantizeValue}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          {isRecording && (
            <span className="flex items-center gap-1 text-red-400">
              <Circle className="w-2 h-2 fill-current animate-pulse" /> Recording
            </span>
          )}
          <span>{sampleRate / 1000}kHz / 24-bit</span>
          <span>Buffer: {bufferSize}</span>
        </div>
      </div>
      
      {/* Audio Settings Modal */}
      <AudioSettingsModal
        isOpen={showAudioSettings}
        onClose={() => setShowAudioSettings(false)}
        audioInputDevices={audioInputDevices}
        audioOutputDevices={audioOutputDevices}
        selectedInputDevice={selectedInputDevice}
        selectedOutputDevice={selectedOutputDevice}
        onInputDeviceChange={setSelectedInputDevice}
        onOutputDeviceChange={setSelectedOutputDevice}
        sampleRate={sampleRate}
        onSampleRateChange={setSampleRate}
        bufferSize={bufferSize}
        onBufferSizeChange={setBufferSize}
        inputChannelConfig={inputChannelConfig}
        onInputChannelConfigChange={setInputChannelConfig}
      />

      {/* Floating Transport — draggable, closable */}
      <FloatingTransport
        visible={floatingTransportVisible}
        onClose={() => setFloatingTransportVisible(false)}
        isPlaying={isPlaying}
        isRecording={isRecording}
        onPlay={togglePlay}
        onStop={stop}
        onRecord={isRecording ? stopRecording : startRecording}
        onRewind={rewind}
        onFastForward={fastForward}
        onGoToStart={goToStart}
        onGoToEnd={goToEnd}
        loopEnabled={loopEnabled}
        onToggleLoop={() => setLoopEnabled(!loopEnabled)}
        currentBeatLabel={formatBars(currentBeat)}
        currentTimeLabel={formatTime(currentBeat)}
        tempo={tempo}
        onTempoChange={setTempo}
      />
      
      {/* Import Audio Modal */}
      <ImportAudioModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        tracks={tracks}
        currentBeat={currentBeat}
        onImport={importAudioFromFile}
      />
    </div>
  );
};

export default StudioSession;
